import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";
import AddCircleIcon from "@material-ui/icons/AddCircle";

import withShipment from "../../withShipment";
import CustomToolbar from "../global/CustomToolbar";
import {
  companiesSelector,
  peopleSelector,
  locationsSelector,
  currentPersonIdSelector,
  savedContractorsSelector,
  contractorCategoriesSelector,
} from "../../redux/selectors/investorTracker";
import { userInfoSelector } from "../../redux/selectors/auth";
import {
  listInvestorTracker,
  saveLocation as saveLocationAction,
  deleteLocation as deleteLocationAction,
  saveExpense as saveExpenseAction,
  deleteExpense as deleteExpenseAction,
  savePayment as savePaymentAction,
  deletePayment as deletePaymentAction,
  updatePaymentStatus as updatePaymentStatusAction,
  updateParticipation as updateParticipationAction,
  saveContractor as saveContractorAction,
  deleteContractor as deleteContractorAction,
  saveQuote as saveQuoteAction,
  deleteQuote as deleteQuoteAction,
  saveContractorPayment as saveContractorPaymentAction,
  deleteContractorPayment as deleteContractorPaymentAction,
  saveWeeklyPayment as saveWeeklyPaymentAction,
  deleteWeeklyPayment as deleteWeeklyPaymentAction,
  saveSavedContractor as saveSavedContractorAction,
  deleteSavedContractor as deleteSavedContractorAction,
  saveContractorCategory as saveContractorCategoryAction,
} from "../../redux/actions/investorTracker";

const BREAKPOINTS = { tablet: 768, desktop: 1024 };

const useViewport = () => {
  const [width, setWidth] = useState(typeof window !== "undefined" ? window.innerWidth : BREAKPOINTS.desktop);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return {
    width,
    isMobile: width < BREAKPOINTS.tablet,
    isTablet: width >= BREAKPOINTS.tablet && width < BREAKPOINTS.desktop,
    isDesktop: width >= BREAKPOINTS.desktop,
    isWide: width >= BREAKPOINTS.tablet,
  };
};


const fmt = (n) => {
  if (n === undefined || n === null || isNaN(n)) return "$0";
  return "$" + Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

// Strip "null" tokens that legacy records sometimes carry from
// upstream firstname+lastname concatenation (e.g. "Admin null").
const cleanName = (n) => {
  if (n == null) return "—";
  const cleaned = String(n).replace(/(?:^|\s)null(?=\s|$)/gi, "").replace(/\s+/g, " ").trim();
  return cleaned || "—";
};

const getPerson = (people, id) => {
  const p = people.find(x => x.id === id);
  if (!p) return { name: "—", id: null, memberships: [] };
  return { ...p, name: cleanName(p.name) };
};

const getRoleInCompany = (person, companyId) => {
  if (!person || !person.memberships) return null;
  const m = person.memberships.find(x => x.companyId === companyId);
  return m ? m.role : null;
};

const personLabel = (person) => cleanName(person && person.name);

const isMember = (person, companyId) => getRoleInCompany(person, companyId) !== null;

const getUserCompanies = (companies, person) => {
  if (!person || !person.memberships) return [];
  const ids = new Set(person.memberships.map(m => m.companyId));
  return companies.filter(c => ids.has(c.id));
};

// "Paid" for an investor on a shared location combines:
//   - the direct contribution stored on their participation row,
//   - every expense they paid for the location (this is how the total grows
//     over time — adding an expense bumps the payer's "paid" and the total),
//   - confirmed inter-investor payments they sent (catching up to their share
//     by reimbursing an over-contributor) minus the ones they received.
// Pending payments don't shift the balance until they're confirmed.
const getEffectivePaid = (loc, personId) => {
  if (!loc || !personId) return 0;
  const part = (loc.participations || []).find(p => p.personId === personId);
  const direct = part ? (Number(part.contributed) || 0) : 0;
  const expensesPaid = (loc.expenses || [])
    .filter(e => e.paidByPersonId === personId)
    .reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const sent = (loc.payments || [])
    .filter(p => p.fromPersonId === personId && p.status === "confirmed")
    .reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const received = (loc.payments || [])
    .filter(p => p.toPersonId === personId && p.status === "confirmed")
    .reduce((s, p) => s + (Number(p.amount) || 0), 0);
  return direct + expensesPaid + sent - received;
};

// For shared locations the total investment is the running sum of effective
// paid across all participants — direct contributions + expenses (inter-
// investor payments cancel between sender and receiver). For financed
// locations it stays the up-front loan principal set on the form.
const getLocationTotal = (loc) => {
  if (!loc) return 0;
  if (loc.type === "financed") {
    const { totalInvestment } = loc;
    return totalInvestment || 0;
  }
  return (loc.participations || []).reduce((s, p) => s + getEffectivePaid(loc, p.personId), 0);
};

const allocate = (amount, weights) => {
  const total = weights.reduce((s, w) => s + w, 0);
  if (total === 0) return weights.map(() => 0);
  const exact = weights.map(w => (w / total) * amount);
  const floored = exact.map(Math.floor);
  let used = floored.reduce((s, v) => s + v, 0);
  let remainder = amount - used;
  const order = exact.map((v, i) => ({ i, frac: v - Math.floor(v) })).sort((a, b) => b.frac - a.frac);
  const result = [...floored];
  for (let k = 0; k < remainder && k < order.length; k++) result[order[k].i]++;
  return result;
};

// Treat legacy weekly payments without an explicit status as `paid`
// so historical data keeps recovering the location total. New payments
// default to `pending` and only flip to `paid` after the operator marks
// them — only `paid` counts toward "recovered" / status.
const isWeeklyPaid = (wp) => (wp.status || "paid") === "paid";

const getRecoveredTotal = (loc) =>
  (loc.weeklyPayments || []).filter(isWeeklyPaid).reduce((s, p) => s + (Number(p.amount) || 0), 0);

const getAccountStatus = (loc) => {
  if (loc.type === "financed") {
    const totalPaid = getRecoveredTotal(loc);
    return totalPaid >= getLocationTotal(loc) ? "settled" : "active";
  }
  const allHavePct = loc.participations.every(p => p.percentage !== null && p.percentage !== undefined);
  if (!allHavePct) return "pending";
  const allSettled = loc.participations.every(p => {
    const share = Math.round(getLocationTotal(loc) * (p.percentage / 100));
    return Math.abs(getEffectivePaid(loc, p.personId) - share) < 1;
  });
  return allSettled ? "settled" : "active";
};

const computeDebts = (loc) => {
  const allHavePct = loc.participations.every(p => p.percentage !== null && p.percentage !== undefined);
  if (!allHavePct) return [];
  const total = getLocationTotal(loc);
  const creditors = [];
  const debtors = [];
  loc.participations.forEach((p, idx) => {
    const share = Math.round(total * (p.percentage / 100));
    const balance = getEffectivePaid(loc, p.personId) - share;
    if (balance > 0) creditors.push({ ...p, overpaid: balance, idx });
    if (balance < 0) debtors.push({ ...p, owes: Math.abs(balance), idx });
  });
  const debts = [];
  debtors.forEach(debtor => {
    const weights = creditors.map(c => c.overpaid);
    const allocations = allocate(debtor.owes, weights);
    creditors.forEach((creditor, ci) => {
      const remaining = allocations[ci];
      if (remaining > 0) {
        // Confirmed payments are already absorbed into the balance, so
        // `remaining` reflects what's still outstanding. Pending payments
        // don't reduce the balance yet but are surfaced for awareness.
        const pending = (loc.payments || [])
          .filter(p => p.fromPersonId === debtor.personId && p.toPersonId === creditor.personId && p.status === "pending")
          .reduce((s, p) => s + (Number(p.amount) || 0), 0);
        debts.push({
          fromPersonId: debtor.personId,
          toPersonId: creditor.personId,
          totalOwed: remaining,
          paid: 0,
          pending,
          remaining,
        });
      }
    });
  });
  return debts;
};

const canSeeAll = (person, companyId) => {
  const role = getRoleInCompany(person, companyId);
  return role === "workspace_owner" || role === "master_admin";
};

// Roster of investor ids attached to a financed location: the legacy
// single financedByPersonId plus the new multi-select roster.
const financedRoster = (loc) => {
  const ids = new Set();
  if (loc.financedByPersonId) ids.add(loc.financedByPersonId);
  (loc.financedInvestorIds || []).forEach(id => ids.add(id));
  return Array.from(ids);
};

const personIsOnLocation = (loc, personId) => {
  if (!personId) return false;
  if (loc.type === "financed") return financedRoster(loc).includes(personId);
  return (loc.participations || []).some(p => p.personId === personId);
};

const getSharedLocations = (locations, viewerId, targetId) => {
  return locations.filter(loc => personIsOnLocation(loc, viewerId) && personIsOnLocation(loc, targetId));
};

const getVisiblePeopleIds = (people, locations, viewerId, companyId) => {
  const viewer = people.find(p => p.id === viewerId);
  if (!viewer) return [];
  const memberPeople = people.filter(p => isMember(p, companyId));
  if (canSeeAll(viewer, companyId)) return memberPeople.map(p => p.id);
  const ids = new Set([viewerId]);
  locations.forEach(loc => {
    if (!personIsOnLocation(loc, viewerId)) return;
    if (loc.type === "financed") financedRoster(loc).forEach(id => ids.add(id));
    (loc.participations || []).forEach(p => ids.add(p.personId));
  });
  return Array.from(ids).filter(id => memberPeople.some(p => p.id === id));
};

const Icons = {
  back: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>,
  plus: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  users: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  check: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  arrowUp: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>,
  arrowDown: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>,
  edit: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  receipt: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16l4-2 4 2 4-2 4 2V8z"/><line x1="9" y1="9" x2="15" y2="9"/></svg>,
  wallet: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><path d="M18 12a2 2 0 0 0 0 4h4v-4z"/></svg>,
  chevron: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>,
  chevronDown: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>,
  hammer: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 12l-8.5 8.5a2.12 2.12 0 1 1-3-3L12 9"/><path d="M17.64 15L22 10.64"/><path d="M20.91 11.7l-1.25-1.25a2.7 2.7 0 0 1 0-3.81l1.55-1.55-2.66-2.66a2.7 2.7 0 0 0-3.81 0L13.18 4"/></svg>,
  building: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><line x1="9" y1="22" x2="9" y2="2"/><line x1="15" y1="22" x2="15" y2="2"/></svg>,
  briefcase: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>,
  person: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  phone: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
};

const PERSON_COLORS = [
  { bg: "#EEF2FF", border: "#818CF8", text: "#4338CA", accent: "#6366F1" },
  { bg: "#ECFDF5", border: "#34D399", text: "#065F46", accent: "#10B981" },
  { bg: "#FFF7ED", border: "#FB923C", text: "#9A3412", accent: "#F97316" },
  { bg: "#FDF2F8", border: "#F472B6", text: "#9D174D", accent: "#EC4899" },
  { bg: "#F0F9FF", border: "#38BDF8", text: "#0C4A6E", accent: "#0EA5E9" },
  { bg: "#FEF3C7", border: "#FBBF24", text: "#92400E", accent: "#F59E0B" },
  { bg: "#F3E8FF", border: "#C084FC", text: "#6B21A8", accent: "#A855F7" },
  { bg: "#FEE2E2", border: "#F87171", text: "#991B1B", accent: "#EF4444" },
  { bg: "#E0F2FE", border: "#7DD3FC", text: "#075985", accent: "#0284C7" },
  { bg: "#DCFCE7", border: "#4ADE80", text: "#166534", accent: "#22C55E" },
  { bg: "#FAE8FF", border: "#E879F9", text: "#86198F", accent: "#D946EF" },
  { bg: "#FFE4E6", border: "#FB7185", text: "#9F1239", accent: "#F43F5E" },
  { bg: "#CFFAFE", border: "#22D3EE", text: "#155E75", accent: "#06B6D4" },
  { bg: "#FEF9C3", border: "#FACC15", text: "#854D0E", accent: "#EAB308" },
  { bg: "#E0E7FF", border: "#A5B4FC", text: "#3730A3", accent: "#4F46E5" },
  { bg: "#D1FAE5", border: "#6EE7B7", text: "#047857", accent: "#059669" },
];

// Build a procedural HSL color for any index past the curated palette
// using the golden-angle hue step so consecutive indices stay visually
// distinct rather than collapsing into similar shades.
const generatedColorAtIndex = (index) => {
  const hue = ((index * 137.508) % 360 + 360) % 360;
  return {
    bg: `hsl(${hue}, 90%, 95%)`,
    border: `hsl(${hue}, 60%, 60%)`,
    text: `hsl(${hue}, 60%, 28%)`,
    accent: `hsl(${hue}, 70%, 48%)`,
  };
};

// Stable-by-id ordering of the people list. Sorting by id (rather than
// hashing) means each person's slot is deterministic and unique across
// reloads, and adding a new person never reshuffles existing colors —
// it just extends past the palette into the procedural HSL range.
const colorForPerson = (personId, peopleList) => {
  if (!personId) return PERSON_COLORS[0];
  if (Array.isArray(peopleList) && peopleList.length > 0) {
    const ordered = peopleList
      .map(p => p && p.id)
      .filter(Boolean)
      .sort();
    const idx = ordered.indexOf(personId);
    if (idx < 0) return PERSON_COLORS[0];
    return idx < PERSON_COLORS.length ? PERSON_COLORS[idx] : generatedColorAtIndex(idx);
  }
  // Fallback when no people list is available (legacy callers) — keep
  // the old hash so we at least don't crash; uniqueness is a best-effort
  // there.
  let hash = 0;
  for (let i = 0; i < personId.length; i++) hash = (hash * 31 + personId.charCodeAt(i)) | 0;
  return PERSON_COLORS[Math.abs(hash) % PERSON_COLORS.length];
};

// Stable per-contractor color. Same palette as people for visual
// cohesion; ordering by contractor id within the location list keeps
// the assignment deterministic and unique within a location.
const colorForContractor = (contractorId, contractorList) => {
  if (!contractorId) return PERSON_COLORS[0];
  if (Array.isArray(contractorList) && contractorList.length > 0) {
    const ordered = contractorList
      .map(c => c && c.id)
      .filter(Boolean)
      .sort();
    const idx = ordered.indexOf(contractorId);
    if (idx < 0) return PERSON_COLORS[0];
    return idx < PERSON_COLORS.length ? PERSON_COLORS[idx] : generatedColorAtIndex(idx);
  }
  let hash = 0;
  const s = String(contractorId);
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) | 0;
  return PERSON_COLORS[Math.abs(hash) % PERSON_COLORS.length];
};

const ACCOUNT_STATUS = {
  active: { bg: "#FEF3C7", text: "#92400E", dot: "#F59E0B", label: "Active" },
  settled: { bg: "#ECFDF5", text: "#065F46", dot: "#10B981", label: "Closed" },
  pending: { bg: "#F0F9FF", text: "#0369A1", dot: "#38BDF8", label: "No % Set" },
};

// Trades a contractor can be tagged with. Picking one of these as a
// contractor's trade also lets the matching expense description route
// the expense to that contractor's ledger.
const CONTRACTOR_TRADES = [
  "Electrical Contractor",
  "Alarm System Contractor",
  "Camera System Contractor",
  "General Contractor",
];

// Categories for the Add Expense dropdown. Editable in Settings (admin only).
// Any category in CONTRACTOR_TRADES requires a contractor pick — selecting
// it routes the expense to the chosen contractor's ledger.
const DEFAULT_EXPENSE_DESCRIPTIONS = [
  "Machine Payments",
  "Electrical Contractor",
  "Alarm System Contractor",
  "Camera System Contractor",
  "General Contractor",
  "Lease / Rent Payment",
  "Permits / Licenses",
  "Utilities",
  "Machine Movers",
  "Parts & Chairs",
  "Other Expense",
];

const GENERAL_CONTRACTOR_DESCRIPTION = "General Contractor";
const isContractorDescription = (d) => CONTRACTOR_TRADES.includes(d);

const expenseDescriptionsStorageKey = (companyId) =>
  `investorTracker.expenseDescriptions.${companyId || "default"}`;

const loadExpenseDescriptions = (companyId) => {
  if (typeof window === "undefined") return [...DEFAULT_EXPENSE_DESCRIPTIONS];
  try {
    const raw = window.localStorage.getItem(expenseDescriptionsStorageKey(companyId));
    if (!raw) return [...DEFAULT_EXPENSE_DESCRIPTIONS];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) return parsed.filter(s => typeof s === "string" && s.trim().length > 0);
  } catch (e) {}
  return [...DEFAULT_EXPENSE_DESCRIPTIONS];
};

const saveExpenseDescriptionsToStorage = (companyId, list) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(expenseDescriptionsStorageKey(companyId), JSON.stringify(list));
  } catch (e) {}
};

// Contractor categories (the user-facing "Category" field on a
// contractor, formerly a hardcoded "trade" list) are now server-synced
// per company — seeded from CONTRACTOR_TRADES on the backend and extended
// when a user types a new one. CONTRACTOR_TRADES stays here only as the
// pre-listAll fallback and to drive expense→contractor routing below.

const FONT = "'DM Sans', sans-serif";
const inputStyle = { width: "100%", padding: "12px 14px", border: "1px solid #E2E8F0", borderRadius: 10, fontSize: 14, fontFamily: FONT, outline: "none", boxSizing: "border-box" };

// Shared BeeNexa palette — aligned with Loans / Profit Tracker so this
// module reads as part of the same product instead of a one-off.
const C = {
  navyDark: "#1E293B",
  navyDeep: "#0F172A",
  primaryGreen: "#2D5A3D",
  accentGreen: "#10B981",
  loanRed: "#E57373",
  deleteRed: "#DC2626",
  warningAmber: "#F59E0B",
  bg: "#F4F6F8",
  cardBg: "#FFFFFF",
  border: "#E5E7EB",
  borderSoft: "#EEF1F4",
  muted: "#64748B",
  mutedSoft: "#94A3B8",
  text: "#1E293B",
  white: "#FFFFFF",
};

const SHADOW_CARD = "0 1px 3px rgba(15,23,42,0.06)";
const SHADOW_HOVER = "0 6px 18px rgba(15,23,42,0.10)";
const SHADOW_HERO = "0 8px 24px rgba(15,23,42,0.18)";

// Hover-lifting wrapper used for every clickable card so the module
// shares the Loans interaction language.
const HoverCard = ({ children, onClick, accent, style = {}, asButton = true }) => {
  const Tag = asButton ? "button" : "div";
  const base = {
    width: "100%",
    background: C.cardBg,
    border: `1px solid ${C.border}`,
    borderRadius: 14,
    padding: 16,
    marginBottom: 10,
    textAlign: "left",
    cursor: onClick ? "pointer" : "default",
    fontFamily: FONT,
    boxShadow: SHADOW_CARD,
    transition: "transform 0.12s ease, box-shadow 0.12s ease",
    ...(accent ? { borderLeft: `4px solid ${accent}` } : {}),
    ...style,
  };
  return (
    <Tag
      onClick={onClick}
      style={base}
      onMouseEnter={onClick ? (e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = SHADOW_HOVER; } : undefined}
      onMouseLeave={onClick ? (e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = SHADOW_CARD; } : undefined}
    >
      {children}
    </Tag>
  );
};

// Calendar-style date picker, mirroring the one used in Loans.
const DP_WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];
const DP_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DP_MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const parseISO = (iso) => {
  if (!iso) return null;
  const parts = String(iso).split("-");
  if (parts.length !== 3) return null;
  const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  return Number.isNaN(d.getTime()) ? null : d;
};

const toISO = (d) => {
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${yr}-${mo}-${da}`;
};

const sameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

// Year-picker view paginates years 12 at a time. Returns the inclusive
// [start, end] range that contains `year`.
const yearPage = (year) => {
  const start = year - ((year % 12) + 12) % 12;
  return [start, start + 11];
};

function DatePicker({ value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  // 'days' | 'months' | 'years' — drill-down for fast month/year jumps.
  const [view, setView] = useState("days");
  const [cursor, setCursor] = useState(() => {
    const init = parseISO(value) || new Date();
    return new Date(init.getFullYear(), init.getMonth(), 1);
  });
  const rootRef = React.useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const openPicker = () => {
    const init = parseISO(value) || new Date();
    setCursor(new Date(init.getFullYear(), init.getMonth(), 1));
    setView("days");
    setOpen(true);
  };
  const prevMonth = () => setCursor(c => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  const nextMonth = () => setCursor(c => new Date(c.getFullYear(), c.getMonth() + 1, 1));
  const prevYear = () => setCursor(c => new Date(c.getFullYear() - 1, c.getMonth(), 1));
  const nextYear = () => setCursor(c => new Date(c.getFullYear() + 1, c.getMonth(), 1));
  const prevYearPage = () => setCursor(c => new Date(c.getFullYear() - 12, c.getMonth(), 1));
  const nextYearPage = () => setCursor(c => new Date(c.getFullYear() + 12, c.getMonth(), 1));
  const selectMonth = (monthIdx) => {
    setCursor(c => new Date(c.getFullYear(), monthIdx, 1));
    setView("days");
  };
  const selectYear = (yr) => {
    setCursor(c => new Date(yr, c.getMonth(), 1));
    setView("months");
  };
  const selectDay = (day) => {
    const d = new Date(cursor.getFullYear(), cursor.getMonth(), day);
    onChange(toISO(d));
    setOpen(false);
  };
  const selectToday = () => {
    const today = new Date();
    onChange(toISO(today));
    setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
    setView("days");
    setOpen(false);
  };
  const cycleView = () => {
    if (view === "days") setView("months");
    else if (view === "months") setView("years");
    else setView("days");
  };

  const selected = parseISO(value);
  const today = new Date();
  const firstWeekday = new Date(cursor.getFullYear(), cursor.getMonth(), 1).getDay();
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  const displayValue = selected ? selected.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <button type="button" onClick={() => { if (open) setOpen(false); else openPicker(); }}
        style={{ width: "100%", padding: "12px 14px", fontSize: 15, fontFamily: FONT, textAlign: "left", border: `2px solid ${open ? C.primaryGreen : C.border}`, borderRadius: 12, background: C.white, color: displayValue ? C.navyDark : C.muted, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", transition: "border-color 0.15s ease", boxSizing: "border-box" }}>
        <span>{displayValue || placeholder || "Select date"}</span>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4 M8 2v4 M3 10h18" />
        </svg>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 8px)", left: 0, right: 0, background: C.white, borderRadius: 16, boxShadow: "0 16px 40px rgba(15, 23, 42, 0.22)", border: `1px solid ${C.border}`, padding: 16, zIndex: 9100, fontFamily: FONT }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <button type="button" onClick={view === "days" ? prevMonth : view === "months" ? prevYear : prevYearPage} style={{ background: "#F1F5F9", border: "none", width: 32, height: 32, borderRadius: 8, cursor: "pointer", color: C.navyDark, display: "flex", alignItems: "center", justifyContent: "center" }} aria-label="Previous">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
            </button>
            <button type="button" onClick={cycleView}
              style={{ background: "transparent", border: "none", fontSize: 15, fontWeight: 700, color: C.navyDark, fontFamily: FONT, cursor: "pointer", padding: "4px 10px", borderRadius: 8, display: "flex", alignItems: "center", gap: 4 }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#F1F5F9"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              aria-label="Switch view">
              {view === "days" && `${DP_MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`}
              {view === "months" && cursor.getFullYear()}
              {view === "years" && (() => { const [s, e] = yearPage(cursor.getFullYear()); return `${s} – ${e}`; })()}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d={view === "years" ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"} />
              </svg>
            </button>
            <button type="button" onClick={view === "days" ? nextMonth : view === "months" ? nextYear : nextYearPage} style={{ background: "#F1F5F9", border: "none", width: 32, height: 32, borderRadius: 8, cursor: "pointer", color: C.navyDark, display: "flex", alignItems: "center", justifyContent: "center" }} aria-label="Next">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
            </button>
          </div>
          {view === "days" ? (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 6 }}>
                {DP_WEEKDAYS.map((w, i) => (
                  <div key={`${w}-${i}`} style={{ textAlign: "center", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, padding: "4px 0" }}>{w}</div>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
                {cells.map((day, idx) => {
                  if (day === null) return <div key={`b-${idx}`} />;
                  const cellDate = new Date(cursor.getFullYear(), cursor.getMonth(), day);
                  const isSelected = sameDay(cellDate, selected);
                  const isToday = sameDay(cellDate, today);
                  let bg = "transparent"; let color = C.navyDark; let fontWeight = 500; let border = "1px solid transparent";
                  if (isSelected) { bg = C.primaryGreen; color = C.white; fontWeight = 700; }
                  else if (isToday) { border = `1px solid ${C.primaryGreen}`; color = C.primaryGreen; fontWeight = 700; }
                  return (
                    <button key={day} type="button" onClick={() => selectDay(day)}
                      style={{ background: bg, color, fontWeight, border, borderRadius: 10, height: 36, cursor: "pointer", fontFamily: FONT, fontSize: 14, transition: "background 0.12s, color 0.12s" }}
                      onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = "#F0FDF4"; }}
                      onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}>
                      {day}
                    </button>
                  );
                })}
              </div>
            </>
          ) : view === "months" ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
              {DP_MONTHS_SHORT.map((m, idx) => {
                const isCurrent = idx === cursor.getMonth();
                const isThisMonth = today.getFullYear() === cursor.getFullYear() && today.getMonth() === idx;
                let bg = "transparent"; let color = C.navyDark; let fontWeight = 500; let border = "1px solid transparent";
                if (isCurrent) { bg = C.primaryGreen; color = C.white; fontWeight = 700; }
                else if (isThisMonth) { border = `1px solid ${C.primaryGreen}`; color = C.primaryGreen; fontWeight = 700; }
                return (
                  <button key={m} type="button" onClick={() => selectMonth(idx)}
                    style={{ background: bg, color, fontWeight, border, borderRadius: 10, height: 48, cursor: "pointer", fontFamily: FONT, fontSize: 14, transition: "background 0.12s, color 0.12s" }}
                    onMouseEnter={(e) => { if (!isCurrent) e.currentTarget.style.background = "#F0FDF4"; }}
                    onMouseLeave={(e) => { if (!isCurrent) e.currentTarget.style.background = "transparent"; }}>
                    {m}
                  </button>
                );
              })}
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
              {(() => {
                const [startYr] = yearPage(cursor.getFullYear());
                const years = [];
                for (let i = 0; i < 12; i += 1) years.push(startYr + i);
                return years.map((yr) => {
                  const isCurrent = yr === cursor.getFullYear();
                  const isThisYear = yr === today.getFullYear();
                  let bg = "transparent"; let color = C.navyDark; let fontWeight = 500; let border = "1px solid transparent";
                  if (isCurrent) { bg = C.primaryGreen; color = C.white; fontWeight = 700; }
                  else if (isThisYear) { border = `1px solid ${C.primaryGreen}`; color = C.primaryGreen; fontWeight = 700; }
                  return (
                    <button key={yr} type="button" onClick={() => selectYear(yr)}
                      style={{ background: bg, color, fontWeight, border, borderRadius: 10, height: 48, cursor: "pointer", fontFamily: FONT, fontSize: 14, transition: "background 0.12s, color 0.12s" }}
                      onMouseEnter={(e) => { if (!isCurrent) e.currentTarget.style.background = "#F0FDF4"; }}
                      onMouseLeave={(e) => { if (!isCurrent) e.currentTarget.style.background = "transparent"; }}>
                      {yr}
                    </button>
                  );
                });
              })()}
            </div>
          )}
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <button type="button" onClick={selectToday} style={{ background: "transparent", border: "none", color: C.primaryGreen, fontFamily: FONT, fontSize: 13, fontWeight: 700, cursor: "pointer", padding: "6px 8px" }}>Today</button>
            <button type="button" onClick={() => setOpen(false)} style={{ background: "transparent", border: "none", color: C.muted, fontFamily: FONT, fontSize: 13, fontWeight: 600, cursor: "pointer", padding: "6px 8px" }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Floating brand-green summary card — replaces the full-width dark
// gradient heroes the v2 spec used. Same shape as Loans' total-
// outstanding card; matches the navbar green so it reads as part of
// the BeeNexa shell.
const SummaryCard = ({ items, accentColor = C.primaryGreen }) => (
  <div style={{
    background: accentColor,
    color: C.white,
    borderRadius: 18,
    padding: "20px 24px",
    display: "grid",
    gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))`,
    gap: 16,
    boxShadow: SHADOW_HERO,
    fontFamily: FONT,
  }}>
    {items.map((item, idx) => (
      <div key={idx} style={{ minWidth: 0, textAlign: idx === items.length - 1 && items.length > 1 ? "right" : "left" }}>
        <div style={{ fontSize: 11, opacity: 0.7, textTransform: "uppercase", letterSpacing: 1.5, fontWeight: 500 }}>{item.label}</div>
        <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4, letterSpacing: -0.5, color: item.color || C.white, overflowWrap: "anywhere" }}>{item.value}</div>
        {item.sub && <div style={{ fontSize: 10, opacity: 0.5, marginTop: 2 }}>{item.sub}</div>}
      </div>
    ))}
  </div>
);

const ACTIVE_COMPANY_KEY = "investmentTracker.activeCompanyId";

const Avatar = ({ personId, size = 32, people }) => {
  const person = getPerson(people, personId);
  const color = colorForPerson(personId, people);
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: color.accent, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: size * 0.4, fontWeight: 700, fontFamily: FONT, flexShrink: 0 }}>
      {cleanName(person.name).charAt(0)}
    </div>
  );
};

const AttributionBadge = ({ personId, people, currentUserId }) => {
  if (!personId) return null;
  const person = getPerson(people, personId);
  const color = colorForPerson(personId, people);
  const isYou = personId === currentUserId;
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "#64748B", fontFamily: FONT, fontStyle: "normal" }}>
      <div style={{ width: 18, height: 18, borderRadius: "50%", background: color.accent, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 10, fontWeight: 700 }}>{cleanName(person.name).charAt(0)}</div>
      <span style={{ fontWeight: 600, color: color.text }}>{cleanName(person.name)}</span>
      {isYou && <span style={{ fontSize: 10, fontWeight: 700, background: "#ECFDF5", color: "#065F46", padding: "2px 6px", borderRadius: 4 }}>YOU</span>}
    </div>
  );
};

const LockedIndicator = () => (
  <div style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 9, color: "#CBD5E1", fontFamily: FONT }}>
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
    Locked
  </div>
);

const EditDeleteButtons = ({ onEdit, onDelete }) => (
  <div style={{ display: "flex", gap: 6 }}>
    <button onClick={onEdit} style={{ background: "#EEF2FF", border: "1px solid #C7D2FE", borderRadius: 6, padding: "4px 6px", cursor: "pointer", color: "#4338CA", display: "flex", alignItems: "center" }}>{Icons.edit}</button>
    <button onClick={onDelete} style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 6, padding: "4px 6px", cursor: "pointer", color: "#991B1B", display: "flex", alignItems: "center" }}>{Icons.trash}</button>
  </div>
);

// Portaled to <body> so the modal escapes ancestor stacking contexts
// (Base.jsx wraps the page in a position:relative / z-index:1 container,
// which would otherwise trap the modal beneath the sticky navbar).
const ModalShell = ({ children, onClose, maxHeight }) => {
  const { isWide } = useViewport();
  if (typeof document === "undefined") return null;
  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: isWide ? "center" : "flex-end", justifyContent: "center", zIndex: 2000, fontFamily: FONT, padding: isWide ? 24 : 0 }}>
      <div onClick={e => e.stopPropagation()} className="bnxa-modal-panel" style={{ background: "white", borderRadius: isWide ? 20 : "20px 20px 0 0", padding: 24, width: "100%", maxWidth: isWide ? 520 : 430, maxHeight, boxShadow: isWide ? "0 20px 60px rgba(0,0,0,0.25)" : "none" }}>{children}</div>
    </div>,
    document.body
  );
};

const ModalHeader = ({ title, onClose }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
    <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1E293B", margin: 0 }}>{title}</h3>
    <button onClick={onClose} style={{ background: "#F1F5F9", border: "none", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 16, color: "#64748B" }}>×</button>
  </div>
);

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ fontSize: 12, fontWeight: 600, color: "#64748B", display: "block", marginBottom: 6 }}>{label}</label>
    {children}
  </div>
);

const PrimaryBtn = ({ onClick, disabled, children, color = "#4F46E5" }) => (
  <button onClick={onClick} disabled={disabled} style={{ width: "100%", padding: "14px", background: disabled ? "#CBD5E1" : color, color: "white", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer", fontFamily: FONT, marginTop: 4 }}>{children}</button>
);

function InvestmentTracker(props) {
  const {
    userInfo,
    companies = [],
    people = [],
    locations = [],
    currentPersonId,
    listInvestorTracker,
    saveLocation,
    deleteLocation,
    saveExpense,
    deleteExpense,
    savePayment,
    deletePayment,
    updatePaymentStatus,
    updateParticipation,
    saveContractor,
    deleteContractor,
    saveQuote,
    deleteQuote,
    saveContractorPayment,
    deleteContractorPayment,
    saveWeeklyPayment,
    deleteWeeklyPayment,
    savedContractors = [],
    saveSavedContractor,
    deleteSavedContractor,
    contractorCategories: contractorCategoriesProp = [],
    saveContractorCategory,
  } = props;

  // Single source of truth for "who am I": currentPersonId comes from
  // the listAll response (server resolves it from the auth user).
  // Falls back to userInfo for the brief moment before listAll lands.
  const realUserId =
    currentPersonId ||
    userInfo?.personId ||
    userInfo?.userId ||
    userInfo?.username ||
    null;

  // Demo user-switcher (spec §1.1, §4.4): workspace_owner / master_admin
  // can preview the module from another investor's perspective. When set,
  // every visibility / scoping decision below uses this id; the real
  // identity (realUserId) only gates the switcher itself.
  const [viewAsPersonId, setViewAsPersonId] = useState(null);
  const currentUserId = viewAsPersonId || realUserId;
  const isImpersonating = viewAsPersonId !== null && viewAsPersonId !== realUserId;

  const { isWide } = useViewport();

  const initialCompanyId = (() => {
    try {
      const stored = typeof window !== "undefined" ? window.localStorage.getItem(ACTIVE_COMPANY_KEY) : null;
      if (stored) return stored;
    } catch (e) {}
    return null;
  })();
  const [activeCompanyId, setActiveCompanyId] = useState(initialCompanyId);

  const [tab, setTab] = useState("locations");
  const [screen, setScreen] = useState("list");
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [selectedPersonId, setSelectedPersonId] = useState(null);
  const [activeLocationTab, setActiveLocationTab] = useState("expenses");
  const [selectedContractorId, setSelectedContractorId] = useState(null);
  const [listFilter, setListFilter] = useState("active");
  const [modal, setModal] = useState(null);
  const [expenseDescriptions, setExpenseDescriptions] = useState(() => loadExpenseDescriptions(initialCompanyId));
  // Contractor categories are server-synced (per company) via listAll /
  // saveContractorCategory. Fall back to the legacy defaults only for the
  // brief moment before listAll lands so the picklist is never empty.
  const contractorCategories = contractorCategoriesProp.length > 0 ? contractorCategoriesProp : CONTRACTOR_TRADES;
  // Tracks which weekly-payment card has its actions menu open. Closing
  // is handled by clicking outside or selecting an action.
  const [weeklyMenuId, setWeeklyMenuId] = useState(null);
  const closeModal = () => setModal(null);

  // Fetch on mount.
  useEffect(() => {
    if (typeof listInvestorTracker === "function") listInvestorTracker({});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const currentUser = getPerson(people, currentUserId);
  const userCompanies = getUserCompanies(companies, currentUser);

  // Pick a default active company once data lands.
  useEffect(() => {
    if (activeCompanyId && companies.some(c => c.id === activeCompanyId)) return;
    if (userCompanies.length > 0) setActiveCompanyId(userCompanies[0].id);
    else if (companies.length > 0) setActiveCompanyId(companies[0].id);
  }, [companies, userCompanies, activeCompanyId]);

  const currentUserRole = getRoleInCompany(currentUser, activeCompanyId);
  const isOwner = currentUserRole === "workspace_owner";
  const isAdmin = currentUserRole === "master_admin";
  const isOwnerOrAdmin = isOwner || isAdmin;

  // Real-identity derivations — used only to gate the user-switcher
  // button. Everything else flows through the effective viewer above.
  const realUser = getPerson(people, realUserId);
  const realUserRole = getRoleInCompany(realUser, activeCompanyId);
  const realCanSwitchUser =
    realUserRole === "workspace_owner" || realUserRole === "master_admin";

  useEffect(() => {
    if (activeCompanyId) {
      try { window.localStorage.setItem(ACTIVE_COMPANY_KEY, activeCompanyId); } catch (e) {}
    }
  }, [activeCompanyId]);

  // Reload the per-company expense descriptions when the active company
  // switches. Saves on every mutation so the next visit picks them up.
  useEffect(() => {
    setExpenseDescriptions(loadExpenseDescriptions(activeCompanyId));
  }, [activeCompanyId]);
  const updateExpenseDescriptions = (next) => {
    setExpenseDescriptions(next);
    saveExpenseDescriptionsToStorage(activeCompanyId, next);
  };

  // Persist a new contractor category to the server. The save endpoint is
  // idempotent (case-insensitive) and returns the full updated list, which
  // the saga writes back into redux — so every member's picklist stays in
  // sync. No-op when the category already exists locally.
  const addContractorCategory = (cat) => {
    const clean = (cat || "").trim();
    if (!clean) return;
    if (contractorCategories.some(c => c.toLowerCase() === clean.toLowerCase())) return;
    if (typeof saveContractorCategory === "function") {
      saveContractorCategory({ companyId: activeCompanyId, name: clean });
    }
  };

  const companyLocations = locations.filter(l => l.companyId === activeCompanyId);
  const companyPeople = people.filter(p => isMember(p, activeCompanyId));

  // Spec §1.2: investors see only locations they participate in (or
  // finance). Owners / master_admins see everything in the company.
  // Used for the Locations tab list, summary totals, and detail-page
  // visibility — the Locations tab previously ignored this and showed
  // every company location regardless of viewer.
  const viewerCanSeeAll = canSeeAll(currentUser, activeCompanyId);
  const viewerLocations = viewerCanSeeAll
    ? companyLocations
    : companyLocations.filter(loc => personIsOnLocation(loc, currentUserId));

  // Mutation handlers — every change is persisted via redux saga, which
  // POSTs to the InvestorTrackerController and upserts the returned location.
  const addExpense = (locId, e, callback) => saveExpense({ locationId: locId, expense: e }, callback);
  const updateExpense = (locId, eId, u) => saveExpense({ locationId: locId, expense: { id: eId, ...u } });
  const removeExpense = (locId, eId) => deleteExpense({ locationId: locId, expenseId: eId });
  const addPayment = (locId, p) => savePayment({ locationId: locId, payment: p });
  const removePayment = (locId, pId) => deletePayment({ locationId: locId, paymentId: pId });
  const confirmPayment = (locId, pId) => updatePaymentStatus({ locationId: locId, paymentId: pId, status: "confirmed" });
  const rejectPayment = (locId, pId) => updatePaymentStatus({ locationId: locId, paymentId: pId, status: "rejected" });
  const updateParticipationLocal = (locId, personId, u) => updateParticipation({ locationId: locId, personId, ...u });
  const removeLocation = (id) => deleteLocation({ locationId: id });
  const addContractor = (locId, c) => saveContractor({ locationId: locId, contractor: c });
  const removeContractor = (locId, cId) => deleteContractor({ locationId: locId, contractorId: cId });
  const addQuote = (locId, cId, q) => saveQuote({ locationId: locId, contractorId: cId, quote: q });
  const removeQuote = (locId, cId, qId) => deleteQuote({ locationId: locId, contractorId: cId, quoteId: qId });
  const addContractorPayment = (locId, cId, p, callback) => saveContractorPayment({ locationId: locId, contractorId: cId, payment: p }, callback);
  const removeContractorPayment = (locId, cId, pId) => deleteContractorPayment({ locationId: locId, contractorId: cId, paymentId: pId });
  const addWeeklyPayment = (locId, wp) => saveWeeklyPayment({ locationId: locId, weeklyPayment: wp });
  const updateWeeklyPayment = (locId, wp) => saveWeeklyPayment({ locationId: locId, weeklyPayment: wp });
  const removeWeeklyPayment = (locId, wpId) => deleteWeeklyPayment({ locationId: locId, weeklyPaymentId: wpId });
  const upsertSavedContractor = (sc, callback) => saveSavedContractor({ savedContractor: { ...sc, companyId: activeCompanyId } }, callback);
  const removeSavedContractorEntry = (id) => deleteSavedContractor({ savedContractorId: id });

  const switchUser = (personId) => {
    setViewAsPersonId(personId === realUserId ? null : personId);
    setScreen("list");
    setSelectedLocationId(null);
    setSelectedPersonId(null);
    setSelectedContractorId(null);
  };

  const renderLocationsList = () => {
    const activeCount = viewerLocations.filter(l => getAccountStatus(l) !== "settled").length;
    const settledCount = viewerLocations.filter(l => getAccountStatus(l) === "settled").length;
    const visible = viewerLocations.filter(l => listFilter === "active" ? getAccountStatus(l) !== "settled" : getAccountStatus(l) === "settled");
    const totalInvested = viewerLocations.reduce((s, l) => s + getLocationTotal(l), 0);
    const pendingForMe = [];
    viewerLocations.forEach(loc => (loc.payments || []).forEach(p => { if (p.status === "pending" && p.toPersonId === currentUserId) pendingForMe.push({ ...p, locationName: loc.name, locationId: loc.id }); }));

    const summaryItems = [
      { label: viewerCanSeeAll ? "All Locations" : "Your Locations", value: fmt(totalInvested), sub: `${viewerLocations.length} location${viewerLocations.length !== 1 ? "s" : ""}` },
    ];
    if (isOwnerOrAdmin) {
      const totalPending = viewerLocations.reduce((s, loc) => s + (loc.payments || []).filter(p => p.status === "pending").length, 0);
      summaryItems.push({ label: "Pending Confirmations", value: totalPending, sub: "across all locations", color: totalPending > 0 ? "#FCD34D" : C.white });
    } else {
      let yc = 0, yl = 0;
      viewerLocations.forEach(l => {
        const part = (l.participations || []).find(p => p.personId === currentUserId);
        if (part) { yc += getEffectivePaid(l, currentUserId); yl++; }
        if (l.type === "financed" && financedRoster(l).includes(currentUserId)) { yc += getLocationTotal(l); yl++; }
      });
      summaryItems.push({ label: "Your Investment", value: fmt(yc), sub: `in ${yl} location${yl !== 1 ? "s" : ""}` });
    }

    return (
      <div>
        <div style={{ padding: "20px 20px 0" }}>
          {realCanSwitchUser && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
              <button
                onClick={() => setModal({ kind: "userSwitcher" })}
                style={{
                  background: C.navyDark,
                  border: `1px solid ${C.navyDark}`,
                  borderRadius: 999,
                  padding: "6px 12px",
                  color: C.white,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontFamily: FONT,
                  boxShadow: SHADOW_CARD,
                }}
              >
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: isImpersonating ? "#FCD34D" : "#34D399" }} />
                {personLabel(currentUser, activeCompanyId)}
                <span style={{ opacity: 0.6, fontSize: 9, marginLeft: 2 }}>{String.fromCharCode(9662)}</span>
              </button>
            </div>
          )}
          <SummaryCard items={summaryItems} />
          {pendingForMe.length > 0 && (
            <div style={{ background: C.primaryGreen, border: `1px solid ${C.primaryGreen}`, borderRadius: 12, padding: "10px 14px", marginTop: 12, fontFamily: FONT, color: C.white }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 1 }}>{pendingForMe.length} payment{pendingForMe.length !== 1 ? "s" : ""} pending your confirmation</div>
              <div style={{ fontSize: 11, opacity: 0.85 }}>Review and confirm if you received them</div>
            </div>
          )}
        </div>
        {isOwnerOrAdmin && (
          <div style={{ display: "flex", marginTop: 16, borderBottom: `2px solid ${C.borderSoft}`, fontFamily: FONT }}>
            <button onClick={() => setTab("locations")} style={{ flex: 1, padding: "12px 18px", border: "none", background: "none", borderBottom: tab === "locations" ? `2px solid ${C.primaryGreen}` : "2px solid transparent", marginBottom: -2, color: tab === "locations" ? C.primaryGreen : C.muted, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: FONT }}>Locations</button>
            <button onClick={() => setTab("people")} style={{ flex: 1, padding: "12px 18px", border: "none", background: "none", borderBottom: tab === "people" ? `2px solid ${C.primaryGreen}` : "2px solid transparent", marginBottom: -2, color: tab === "people" ? C.primaryGreen : C.muted, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: FONT }}>People</button>
          </div>
        )}
        {(!isOwnerOrAdmin || tab === "locations") ? (
          <div style={{ padding: "16px 20px 100px" }}>
            {companyLocations.length === 0 ? (
              <div style={{ background: C.white, border: `1px dashed ${C.border}`, borderRadius: 14, padding: 48, textAlign: "center", marginTop: 8, boxShadow: SHADOW_CARD }}>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 10, color: C.primaryGreen }}>{Icons.building}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 4 }}>No locations yet</div>
                <div style={{ fontSize: 13, color: C.mutedSoft, marginBottom: 16 }}>Add your first location to get started</div>
                <button onClick={() => setModal({ kind: "addLocation" })} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: C.primaryGreen, color: C.white, border: "none", borderRadius: 10, padding: "10px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>
                  {Icons.plus} New Location
                </button>
              </div>
            ) : (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, marginTop: 4 }}>
                  <h2 style={{ fontSize: 16, fontWeight: 700, color: C.text, margin: 0, fontFamily: FONT }}>All Locations</h2>
                </div>
                {isOwnerOrAdmin && (
                  <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
                    <div style={{ flex: 1 }} />
                    <button
                      type="button"
                      onClick={() => setModal({ kind: "expenseDescriptionsSettings" })}
                      title="Manage expense descriptions"
                      style={{ padding: "8px 14px", border: `1px solid ${C.border}`, background: C.white, color: C.muted, borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: FONT, display: "inline-flex", alignItems: "center", gap: 6 }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="3" />
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                      </svg>
                      Expense Descriptions
                    </button>
                    <button
                      type="button"
                      onClick={() => setModal({ kind: "savedContractors" })}
                      title="Manage saved contractors"
                      style={{ padding: "8px 14px", border: `1px solid ${C.border}`, background: C.white, color: C.muted, borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: FONT, display: "inline-flex", alignItems: "center", gap: 6 }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 12l-8.5 8.5a2.12 2.12 0 1 1-3-3L12 9" />
                        <path d="M17.64 15L22 10.64" />
                        <path d="M20.91 11.7l-1.25-1.25a2.7 2.7 0 0 1 0-3.81l1.55-1.55-2.66-2.66a2.7 2.7 0 0 0-3.81 0L13.18 4" />
                      </svg>
                      Contractors
                      {savedContractors.length > 0 && (
                        <span style={{ fontSize: 10, fontWeight: 700, background: "#F1F5F9", color: C.muted, padding: "1px 7px", borderRadius: 999 }}>{savedContractors.length}</span>
                      )}
                    </button>
                  </div>
                )}
                <div style={{ display: "flex", background: C.borderSoft, borderRadius: 12, padding: 4, marginBottom: 16, maxWidth: 360 }}>
                  <button onClick={() => setListFilter("active")} style={{ flex: 1, padding: "8px 12px", border: "none", background: listFilter === "active" ? C.primaryGreen : "transparent", color: listFilter === "active" ? C.white : C.muted, borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: FONT, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, boxShadow: listFilter === "active" ? SHADOW_CARD : "none" }}>
                    Active <span style={{ background: listFilter === "active" ? "#DCFCE7" : "#E2E8F0", color: listFilter === "active" ? "#065F46" : C.muted, fontSize: 10, padding: "1px 6px", borderRadius: 8 }}>{activeCount}</span>
                  </button>
                  <button onClick={() => setListFilter("settled")} style={{ flex: 1, padding: "8px 12px", border: "none", background: listFilter === "settled" ? C.primaryGreen : "transparent", color: listFilter === "settled" ? C.white : C.muted, borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: FONT, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, boxShadow: listFilter === "settled" ? SHADOW_CARD : "none" }}>
                    Closed <span style={{ background: listFilter === "settled" ? "#DCFCE7" : "#E2E8F0", color: listFilter === "settled" ? "#065F46" : C.muted, fontSize: 10, padding: "1px 6px", borderRadius: 8 }}>{settledCount}</span>
                  </button>
                </div>
                <div style={{ display: isWide ? "grid" : "block", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(360px, 1fr))" : undefined, gap: isWide ? 12 : 0 }}>
                {visible.map(loc => {
                  const isFinanced = loc.type === "financed";
                  const typeBadge = isFinanced
                    ? { bg: "#EEF2FF", text: "#4338CA", dot: "#6366F1", label: "Financed" }
                    : { bg: "#ECFDF5", text: "#065F46", dot: "#10B981", label: "Shared" };
                  return (
                    <HoverCard key={loc.id} accent={C.primaryGreen} onClick={() => { setSelectedLocationId(loc.id); setActiveLocationTab("expenses"); setScreen("locationDetail"); }} style={{ marginBottom: isWide ? 0 : 10 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 12 }}>
                          <div style={{ width: 38, height: 38, borderRadius: 10, background: "#F0FDF4", color: C.primaryGreen, display: "flex", alignItems: "center", justifyContent: "center" }}>{Icons.building}</div>
                          <div>
                            <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{loc.name}</div>
                            <div style={{ fontSize: 12, color: C.mutedSoft }}>{loc.address}</div>
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 4, background: typeBadge.bg, color: typeBadge.text, padding: "4px 10px", borderRadius: 999, fontSize: 10, fontWeight: 700 }}>
                          <div style={{ width: 5, height: 5, borderRadius: "50%", background: typeBadge.dot }} />{typeBadge.label}
                        </div>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 10, borderTop: `1px solid ${C.borderSoft}` }}>
                        <div>
                          <div style={{ fontSize: 10, color: C.mutedSoft, marginBottom: 2, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>Total</div>
                          <div style={{ fontSize: 17, fontWeight: 700, color: C.text }}>{fmt(getLocationTotal(loc))}</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: 10, color: C.mutedSoft, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>{loc.type === "financed" ? (financedRoster(loc).length > 1 ? "Investors" : "Financed By") : "Investors"}</div>
                          {loc.type === "financed" ? (
                            (() => {
                              const fr = financedRoster(loc);
                              if (fr.length <= 1) {
                                return <div style={{ fontSize: 12, fontWeight: 700, color: C.primaryGreen }}>{getPerson(people, loc.financedByPersonId).name}</div>;
                              }
                              return (
                                <div style={{ display: "flex", justifyContent: "center" }}>
                                  {fr.slice(0, 4).map((id, i) => (
                                    <div key={id} style={{ marginLeft: i > 0 ? -6 : 0, border: `2px solid ${C.white}`, borderRadius: "50%" }}>
                                      <Avatar personId={id} size={24} people={people} />
                                    </div>
                                  ))}
                                  {fr.length > 4 && (
                                    <div style={{ marginLeft: -6, width: 24, height: 24, borderRadius: "50%", background: "#E2E8F0", color: "#475569", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, border: `2px solid ${C.white}` }}>+{fr.length - 4}</div>
                                  )}
                                </div>
                              );
                            })()
                          ) : (
                            <div style={{ display: "flex", justifyContent: "center" }}>
                              {loc.participations.map((p, i) => (
                                <div key={p.personId} style={{ marginLeft: i > 0 ? -6 : 0, border: `2px solid ${C.white}`, borderRadius: "50%" }}>
                                  <Avatar personId={p.personId} size={24} people={people} />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: 10, color: C.mutedSoft, marginBottom: 2, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>Opened</div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: C.muted }}>{loc.openDate}</div>
                        </div>
                      </div>
                    </HoverCard>
                  );
                })}
                </div>
                {visible.length === 0 && (
                  <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 14, padding: 40, textAlign: "center", boxShadow: SHADOW_CARD }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.mutedSoft }}>No {listFilter === "active" ? "active" : "closed"} locations</div>
                  </div>
                )}
              </>
            )}
          </div>
        ) : renderPeopleList()}
      </div>
    );
  };

  const renderPeopleList = () => {
    const visibleIds = getVisiblePeopleIds(people, companyLocations, currentUserId, activeCompanyId);
    const investorPeople = people.filter(p => visibleIds.includes(p.id));
    const aggregates = investorPeople.map(person => {
      let totalContributed = 0, totalShareCost = 0, locationCount = 0, sharedLocationCount = 0;
      let pendingForMe = 0, pendingFromMe = 0;
      const viewerCanSeeAll = canSeeAll(currentUser, activeCompanyId);
      const scopedLocations = viewerCanSeeAll ? companyLocations : (person.id === currentUserId ? companyLocations.filter(loc => personIsOnLocation(loc, currentUserId)) : getSharedLocations(companyLocations, currentUserId, person.id));
      scopedLocations.forEach(loc => {
        if (loc.type === "financed") {
          // Financed locations don't have ownership shares — they're loans
          // with weekly repayments. Skip them in Paid/Share/Balance so the
          // tiles only reflect equity-style relationships.
          if (financedRoster(loc).includes(person.id)) {
            locationCount++;
          }
        } else {
          const part = (loc.participations || []).find(p => p.personId === person.id);
          if (part) {
            totalContributed += getEffectivePaid(loc, person.id);
            if (part.percentage) totalShareCost += Math.round(getLocationTotal(loc) * (part.percentage / 100));
            locationCount++;
            sharedLocationCount++;
          }
        }
        (loc.payments || []).forEach(p => {
          if (p.status === "pending") {
            if (p.toPersonId === person.id) pendingForMe++;
            if (p.fromPersonId === person.id) pendingFromMe++;
          }
        });
      });
      return { person, totalContributed, totalShareCost, netBalance: totalContributed - totalShareCost, locationCount, sharedLocationCount, pendingForMe, pendingFromMe };
    });
    aggregates.sort((a, b) => b.totalContributed - a.totalContributed);
    return (
      <div style={{ padding: "16px 20px 100px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: C.text, margin: 0, fontFamily: FONT }}>People</h2>
          <span style={{ fontSize: 11, color: C.mutedSoft, fontFamily: FONT }}>{investorPeople.length} {investorPeople.length === 1 ? "person" : "people"}</span>
        </div>
        <div style={{ background: "#F0FDF4", border: `1px solid #BBF7D0`, borderRadius: 12, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: C.primaryGreen, fontFamily: FONT, lineHeight: 1.5 }}>
          {canSeeAll(currentUser, activeCompanyId)
            ? `Per-person totals across all locations. Each location remains independent — investors with no relationship are not netted.`
            : `You only see people you share a location with. Totals shown reflect only those shared locations.`}
        </div>
        {aggregates.length === 0 ? (
          <div style={{ background: C.white, border: `1px dashed ${C.border}`, borderRadius: 14, padding: 40, textAlign: "center", boxShadow: SHADOW_CARD }}>
            <div style={{ fontSize: 14, color: C.mutedSoft, fontWeight: 600 }}>No people visible yet</div>
          </div>
        ) : (
        <div style={{ display: isWide ? "grid" : "block", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(360px, 1fr))" : undefined, gap: isWide ? 12 : 0 }}>
        {aggregates.map(({ person, totalContributed, totalShareCost, netBalance, locationCount, sharedLocationCount, pendingForMe, pendingFromMe }) => {
          const color = colorForPerson(person.id, people);
          const isYou = person.id === currentUserId;
          return (
            <HoverCard key={person.id} accent={color.accent} onClick={() => { setSelectedPersonId(person.id); setScreen("personDetail"); }} style={{ marginBottom: isWide ? 0 : 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <Avatar personId={person.id} size={42} people={people} />
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{cleanName(person.name)}</span>
                      {isYou && <span style={{ fontSize: 9, fontWeight: 700, background: "#ECFDF5", color: "#065F46", padding: "2px 6px", borderRadius: 4 }}>YOU</span>}
                    </div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{locationCount} location{locationCount !== 1 ? "s" : ""}</div>
                  </div>
                </div>
              </div>
              {sharedLocationCount > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)", gap: 8 }}>
                  <div style={{ background: C.borderSoft, borderRadius: 10, padding: "10px 12px" }}>
                    <div style={{ fontSize: 9, color: C.muted, fontWeight: 700, marginBottom: 2, textTransform: "uppercase", letterSpacing: 0.5 }}>Paid</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{fmt(totalContributed)}</div>
                  </div>
                  <div style={{ background: C.borderSoft, borderRadius: 10, padding: "10px 12px" }}>
                    <div style={{ fontSize: 9, color: C.muted, fontWeight: 700, marginBottom: 2, textTransform: "uppercase", letterSpacing: 0.5 }}>Share</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{fmt(totalShareCost)}</div>
                  </div>
                  <div style={{ background: netBalance >= 0 ? "#DCFCE7" : "#FEE2E2", borderRadius: 10, padding: "10px 12px" }}>
                    <div style={{ fontSize: 9, color: netBalance >= 0 ? "#065F46" : "#991B1B", fontWeight: 700, marginBottom: 2, textTransform: "uppercase", letterSpacing: 0.5 }}>Balance</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: netBalance >= 0 ? "#065F46" : "#991B1B", display: "flex", alignItems: "center", gap: 2 }}>
                      {netBalance >= 0 ? Icons.arrowUp : Icons.arrowDown}{fmt(netBalance)}
                    </div>
                  </div>
                </div>
              )}
              {(pendingForMe > 0 || pendingFromMe > 0) && (
                <div style={{ marginTop: 10, padding: "6px 10px", background: "#FEF3C7", borderRadius: 8, fontSize: 11, color: "#92400E", fontWeight: 600 }}>
                  {pendingForMe > 0 && <span>{pendingForMe} pending receipt{pendingForMe !== 1 ? "s" : ""}</span>}
                  {pendingForMe > 0 && pendingFromMe > 0 && " " + String.fromCharCode(183) + " "}
                  {pendingFromMe > 0 && <span>{pendingFromMe} sent pending</span>}
                </div>
              )}
            </HoverCard>
          );
        })}
        </div>
        )}
      </div>
    );
  };

  const renderPersonDetail = () => {
    const person = getPerson(people, selectedPersonId);
    if (!person.id) return null;
    const color = colorForPerson(person.id, people);
    const isYou = person.id === currentUserId;
    const personLocations = [];
    const viewerCanSeeAll = canSeeAll(currentUser, activeCompanyId);
    const scopedLocations = viewerCanSeeAll
      ? companyLocations
      : (person.id === currentUserId
        ? companyLocations.filter(loc => personIsOnLocation(loc, currentUserId))
        : getSharedLocations(companyLocations, currentUserId, person.id));
    scopedLocations.forEach(loc => {
      if (loc.type === "financed") {
        if (financedRoster(loc).includes(person.id)) {
          // Only the original financier (financedByPersonId) actually
          // outlaid the principal — other roster members just make
          // weekly repayments to that person. Show money tiles for the
          // financier; render the others' card without numbers.
          const isPrimary = loc.financedByPersonId === person.id;
          const paid = isPrimary ? getRecoveredTotal(loc) : null;
          const total = isPrimary ? getLocationTotal(loc) : null;
          personLocations.push({
            loc,
            kind: "financed",
            isPrimary,
            contributed: isPrimary ? total : null,
            shareCost: isPrimary ? paid : null,
            balance: isPrimary ? total - paid : null,
          });
        }
      } else {
        const part = (loc.participations || []).find(p => p.personId === person.id);
        if (part) {
          const shareCost = part.percentage ? Math.round(getLocationTotal(loc) * (part.percentage / 100)) : null;
          const paid = getEffectivePaid(loc, person.id);
          personLocations.push({ loc, kind: "shared", participation: part, contributed: paid, shareCost, balance: shareCost !== null ? paid - shareCost : null });
        }
      }
    });
    const totals = personLocations.reduce((acc, pl) => {
      if (pl.contributed !== null) acc.contributed += pl.contributed;
      if (pl.shareCost !== null) acc.shareCost += pl.shareCost;
      if (pl.balance !== null) acc.balance += pl.balance;
      return acc;
    }, { contributed: 0, shareCost: 0, balance: 0 });
    return (
      <div>
        <div style={{ background: `linear-gradient(135deg, ${color.accent} 0%, ${color.text} 100%)`, padding: isWide ? "32px 32px 36px" : "20px 20px 24px", color: C.white, fontFamily: FONT }}>
          <div style={{ display: "flex", alignItems: "center", gap: isWide ? 18 : 12, marginBottom: isWide ? 20 : 14, flexWrap: isWide ? "nowrap" : "wrap" }}>
            <div style={{ width: isWide ? 72 : 56, height: isWide ? 72 : 56, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: isWide ? 32 : 24, fontWeight: 700, fontFamily: FONT, flexShrink: 0 }}>{cleanName(person.name).charAt(0)}</div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <h1 style={{ fontSize: isWide ? 28 : 22, fontWeight: 700, margin: 0, letterSpacing: -0.5, wordBreak: "break-word" }}>{cleanName(person.name)}</h1>
                {isYou && <span style={{ fontSize: 10, fontWeight: 700, background: "rgba(255,255,255,0.25)", padding: "3px 8px", borderRadius: 4, letterSpacing: 0.5 }}>YOU</span>}
              </div>
              {person.email && <div style={{ fontSize: isWide ? 14 : 13, opacity: 0.9, marginTop: 4, wordBreak: "break-word" }}>{person.email}</div>}
              {person.phone && <div style={{ fontSize: isWide ? 13 : 12, opacity: 0.8 }}>{person.phone}</div>}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)", gap: isWide ? 14 : 10 }}>
            <div style={{ background: "rgba(255,255,255,0.15)", borderRadius: 12, padding: isWide ? "14px 16px" : "12px 14px", minWidth: 0 }}>
              <div style={{ fontSize: 10, opacity: 0.85, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700, marginBottom: 4 }}>Paid</div>
              <div style={{ fontSize: isWide ? 22 : 17, fontWeight: 700, letterSpacing: -0.5, overflowWrap: "anywhere" }}>{fmt(totals.contributed)}</div>
            </div>
            <div style={{ background: "rgba(255,255,255,0.15)", borderRadius: 12, padding: isWide ? "14px 16px" : "12px 14px", minWidth: 0 }}>
              <div style={{ fontSize: 10, opacity: 0.85, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700, marginBottom: 4 }}>Share</div>
              <div style={{ fontSize: isWide ? 22 : 17, fontWeight: 700, letterSpacing: -0.5, overflowWrap: "anywhere" }}>{fmt(totals.shareCost)}</div>
            </div>
            <div style={{ background: "rgba(255,255,255,0.15)", borderRadius: 12, padding: isWide ? "14px 16px" : "12px 14px", minWidth: 0 }}>
              <div style={{ fontSize: 10, opacity: 0.85, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700, marginBottom: 4 }}>Balance</div>
              <div style={{ fontSize: isWide ? 22 : 17, fontWeight: 700, letterSpacing: -0.5, display: "flex", alignItems: "center", gap: 2, overflowWrap: "anywhere" }}>
                {totals.balance >= 0 ? Icons.arrowUp : Icons.arrowDown}{fmt(totals.balance)}
              </div>
            </div>
          </div>
        </div>
        <div style={{ padding: "20px 20px 100px" }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: C.text, margin: "0 0 14px", fontFamily: FONT }}>Locations ({personLocations.length})</h3>
          {personLocations.length === 0 ? (
            <div style={{ background: C.white, border: `1px dashed ${C.border}`, borderRadius: 14, padding: 40, textAlign: "center", boxShadow: SHADOW_CARD }}>
              <div style={{ fontSize: 14, color: C.mutedSoft, fontWeight: 600 }}>No locations yet</div>
            </div>
          ) : (
            <div style={{ display: isWide ? "grid" : "block", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(360px, 1fr))" : undefined, gap: isWide ? 12 : 0 }}>
            {personLocations.map(({ loc, kind, participation, isPrimary, contributed, shareCost, balance }) => {
              const status = getAccountStatus(loc);
              const sInfo = ACCOUNT_STATUS[status];
              const showTiles = contributed !== null;
              return (
                <HoverCard key={loc.id} accent={C.primaryGreen} onClick={() => { setSelectedLocationId(loc.id); setActiveLocationTab("overview"); setScreen("locationDetail"); }} style={{ marginBottom: isWide ? 0 : 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: showTiles ? 12 : 0 }}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{loc.name}</span>
                        {kind === "financed" && <span style={{ fontSize: 9, fontWeight: 700, background: "#EEF2FF", color: "#4338CA", padding: "2px 6px", borderRadius: 4 }}>FINANCED</span>}
                        {kind === "financed" && isPrimary && <span style={{ fontSize: 9, fontWeight: 700, background: "#FEF3C7", color: "#92400E", padding: "2px 6px", borderRadius: 4 }}>FINANCIER</span>}
                        {kind === "financed" && !isPrimary && <span style={{ fontSize: 9, fontWeight: 700, background: "#F1F5F9", color: "#475569", padding: "2px 6px", borderRadius: 4 }}>PAYER</span>}
                        {kind === "shared" && participation.percentage && <span style={{ fontSize: 10, fontWeight: 700, background: color.bg, color: color.text, padding: "2px 6px", borderRadius: 4 }}>{participation.percentage}%</span>}
                      </div>
                      <div style={{ fontSize: 12, color: C.mutedSoft }}>{loc.address}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, background: sInfo.bg, color: sInfo.text, padding: "3px 9px", borderRadius: 999, fontSize: 10, fontWeight: 700 }}>
                      <div style={{ width: 5, height: 5, borderRadius: "50%", background: sInfo.dot }} />{sInfo.label}
                    </div>
                  </div>
                  {showTiles && (
                    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)", gap: 10, paddingTop: 10, borderTop: `1px solid ${C.borderSoft}` }}>
                      <div><div style={{ fontSize: 9, color: C.mutedSoft, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>Paid</div><div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{fmt(contributed)}</div></div>
                      <div><div style={{ fontSize: 9, color: C.mutedSoft, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>{kind === "financed" ? "Recovered" : "Share"}</div><div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{shareCost !== null ? fmt(shareCost) : "—"}</div></div>
                      <div><div style={{ fontSize: 9, color: C.mutedSoft, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>Balance</div><div style={{ fontSize: 14, fontWeight: 700, color: balance === null ? C.mutedSoft : balance >= 0 ? "#065F46" : "#991B1B" }}>{balance !== null ? fmt(balance) : "—"}</div></div>
                    </div>
                  )}
                </HoverCard>
              );
            })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderLocationDetail = () => {
    const loc = locations.find(l => l.id === selectedLocationId);
    if (!loc) return null;
    if (loc.companyId !== activeCompanyId) {
      setScreen("list");
      return null;
    }
    // Spec §1.2: bounce out if the (possibly-impersonated) viewer can't
    // see this location. Prevents lingering selections from leaking
    // detail when an owner switches into an investor's perspective.
    if (!viewerLocations.some(l => l.id === loc.id)) {
      setScreen("list");
      return null;
    }
    const status = getAccountStatus(loc);
    const sInfo = ACCOUNT_STATUS[status];
    return (
      <div>
        <div style={{ padding: "20px 20px 0" }}>
          <SummaryCard items={[
            { label: "Location", value: loc.name, sub: `${loc.address || ""}${loc.openDate ? " · Opened " + loc.openDate : ""}` },
            { label: "Total Investment", value: fmt(getLocationTotal(loc)), sub: sInfo.label.toUpperCase() },
          ]} />
          {isOwnerOrAdmin && (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
              <button onClick={() => setModal({ kind: "editLocation", locationId: loc.id })} style={{ background: "#EEF2FF", border: `1px solid #C7D2FE`, color: "#4338CA", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, fontFamily: FONT }}>{Icons.edit} Edit Location</button>
              {isOwner && (
                <button onClick={() => setModal({ kind: "deleteLocation", locationId: loc.id })} style={{ background: "#FEF2F2", border: `1px solid #FECACA`, color: C.deleteRed, borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, fontFamily: FONT }}>{Icons.trash} Delete Location</button>
              )}
            </div>
          )}
        </div>
        {loc.type === "financed" ? renderFinancedDetail(loc) : (
          <>
            <div className="bnxa-tab-strip" style={{ display: "flex", overflowX: isWide ? "visible" : "auto", scrollbarWidth: "none" }}>
              {[{ k: "expenses", l: "Expenses", i: Icons.receipt }, { k: "quotes", l: "Contractors", i: Icons.hammer }, { k: "overview", l: "Investors", i: Icons.users }, { k: "payments", l: "Payments", i: Icons.wallet }].map(t => {
                const isActive = activeLocationTab === t.k;
                return (
                  <button
                    key={t.k}
                    className={"bnxa-tab-btn" + (isActive ? " bnxa-tab-btn-active" : "")}
                    onClick={() => { setActiveLocationTab(t.k); if (t.k !== "quotes") setSelectedContractorId(null); }}
                    style={{ flex: isWide ? 1 : "0 0 auto", padding: "10px 16px 12px", color: isActive ? C.primaryGreen : C.muted, fontWeight: isActive ? 700 : 600, fontSize: 13, cursor: "pointer", fontFamily: FONT, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, whiteSpace: "nowrap" }}
                  >
                    {t.i} {t.l}
                  </button>
                );
              })}
            </div>
            <div style={{ padding: "20px 20px 100px" }}>
              {activeLocationTab === "overview" && renderInvestorsTab(loc)}
              {activeLocationTab === "expenses" && renderExpensesTab(loc)}
              {activeLocationTab === "quotes" && renderQuotesTab(loc)}
              {activeLocationTab === "payments" && renderPaymentsTab(loc)}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderFinancedDetail = (loc) => {
    const totalPaid = getRecoveredTotal(loc);
    const pendingTotal = (loc.weeklyPayments || []).filter(p => !isWeeklyPaid(p)).reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const remaining = Math.max(0, getLocationTotal(loc) - totalPaid);
    const total = getLocationTotal(loc);
    const progressPct = total > 0 ? Math.min(100, Math.round((totalPaid / total) * 100)) : 0;
    const isComplete = remaining <= 0;
    const financedBy = getPerson(people, loc.financedByPersonId);
    const roster = financedRoster(loc);
    const investorOptions = (roster.length > 0 ? roster : people.map(p => p.id))
      .map(id => getPerson(people, id))
      .filter(p => p.id);
    return (
      <div style={{ padding: "16px 20px 100px" }}>
        <div style={{ background: "white", border: "1px solid #818CF830", borderLeft: "4px solid #6366F1", borderRadius: 14, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: "#475569", fontWeight: 700, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>Financed By</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
            <Avatar personId={loc.financedByPersonId} size={40} people={people} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#1E293B" }}>{financedBy.name}</div>
              <div style={{ fontSize: 13, color: "#64748B" }}>
                {roster.length > 1 ? `${roster.length} investors on this location` : "Paid the full investment upfront"}
              </div>
            </div>
            {roster.length > 1 && (
              <div style={{ display: "flex" }}>
                {roster.slice(0, 5).map((id, i) => (
                  <div key={id} style={{ marginLeft: i > 0 ? -6 : 0, border: "2px solid #FFFFFF", borderRadius: "50%" }}>
                    <Avatar personId={id} size={24} people={people} />
                  </div>
                ))}
                {roster.length > 5 && (
                  <div style={{ marginLeft: -6, width: 24, height: 24, borderRadius: "50%", background: "#E2E8F0", color: "#475569", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, border: "2px solid #FFFFFF" }}>+{roster.length - 5}</div>
                )}
              </div>
            )}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isWide ? "1fr 1fr 1fr 1fr" : "1fr 1fr", gap: 8, marginBottom: 14 }}>
            <div style={{ background: "#F0F1F5", borderRadius: 10, padding: "10px 12px", minWidth: 0 }}>
              <div style={{ fontSize: 11, color: "#475569", fontWeight: 700, marginBottom: 2, textTransform: "uppercase" }}>Invested</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#1E293B", wordBreak: "break-word" }}>{fmt(getLocationTotal(loc))}</div>
            </div>
            <div style={{ background: "#ECFDF5", borderRadius: 10, padding: "10px 12px", minWidth: 0 }}>
              <div style={{ fontSize: 11, color: "#065F46", fontWeight: 700, marginBottom: 2, textTransform: "uppercase" }}>Recovered</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#065F46", wordBreak: "break-word" }}>{fmt(totalPaid)}</div>
            </div>
            <div style={{ background: "#FEF3C7", borderRadius: 10, padding: "10px 12px", minWidth: 0 }}>
              <div style={{ fontSize: 11, color: "#92400E", fontWeight: 700, marginBottom: 2, textTransform: "uppercase" }}>Pending</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#92400E", wordBreak: "break-word" }}>{fmt(pendingTotal)}</div>
            </div>
            <div style={{ background: isComplete ? "#ECFDF5" : "#FEE2E2", borderRadius: 10, padding: "10px 12px", minWidth: 0 }}>
              <div style={{ fontSize: 11, color: isComplete ? "#065F46" : "#991B1B", fontWeight: 700, marginBottom: 2, textTransform: "uppercase" }}>Remaining</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: isComplete ? "#065F46" : "#DC2626", wordBreak: "break-word" }}>{fmt(remaining)}</div>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>Progress</span>
            <span style={{ fontSize: 12, color: isComplete ? "#065F46" : "#4F46E5", fontWeight: 700 }}>{progressPct}%</span>
          </div>
          <div style={{ height: 10, borderRadius: 5, background: "#E2E8F0", overflow: "hidden" }}>
            <div style={{ height: "100%", borderRadius: 5, width: `${progressPct}%`, background: isComplete ? "#10B981" : "linear-gradient(90deg, #4F46E5, #7C3AED)" }} />
          </div>
          {isComplete && <div style={{ marginTop: 12, padding: "10px 14px", background: "#ECFDF5", borderRadius: 10, fontSize: 12, fontWeight: 700, color: "#065F46", textAlign: "center" }}>Investment fully recovered</div>}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: "#1E293B", margin: 0, fontFamily: FONT }}>Weekly Payments</h3>
          <button onClick={() => setModal({ kind: "addWeeklyPayment", locationId: loc.id })} style={{ display: "flex", alignItems: "center", gap: 4, background: "#4F46E5", color: "white", border: "none", borderRadius: 10, padding: "8px 12px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>{Icons.plus} Add</button>
        </div>
        {(loc.weeklyPayments || []).slice().reverse().map(wp => {
          const paid = isWeeklyPaid(wp);
          const badge = paid
            ? { bg: "#ECFDF5", text: "#065F46", dot: "#10B981", label: "Paid" }
            : { bg: "#FEF3C7", text: "#92400E", dot: "#F59E0B", label: "Pending" };
          const payer = wp.paidByPersonId ? getPerson(people, wp.paidByPersonId) : null;
          const paidDateLabel = wp.paidDate || (paid ? wp.date : null);
          const menuOpen = weeklyMenuId === wp.id;
          const payerColor = payer && payer.id ? colorForPerson(payer.id, people) : null;
          return (
            <div key={wp.id} style={{ position: "relative", background: "white", border: "1px solid #E2E8F0", borderLeft: `4px solid ${badge.dot}`, borderRadius: 12, padding: 12, marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: "#1E293B" }}>{wp.week || "—"}</span>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: badge.bg, color: badge.text, padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700 }}>
                      <span style={{ width: 5, height: 5, borderRadius: "50%", background: badge.dot }} />{badge.label}
                    </span>
                  </div>
                  {payer && payer.id ? (
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px 4px 4px", background: payerColor.bg, border: `1px solid ${payerColor.accent}40`, borderRadius: 999, marginBottom: 6 }}>
                      <Avatar personId={payer.id} size={20} people={people} />
                      <span style={{ fontSize: 13, fontWeight: 700, color: payerColor.text }}>Paid by {payer.name}</span>
                    </div>
                  ) : (
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", background: "#F1F5F9", border: "1px dashed #CBD5E1", borderRadius: 999, marginBottom: 6 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#64748B" }}>No payer assigned</span>
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: "#94A3B8", display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {wp.date && <span>Logged {wp.date}</span>}
                    {paidDateLabel && paidDateLabel !== wp.date && <><span>·</span><span>{paid ? "Paid" : "Updated"} {paidDateLabel}</span></>}
                  </div>
                  {wp.note && <div style={{ fontSize: 13, color: "#64748B", marginTop: 6, fontStyle: "italic" }}>"{wp.note}"</div>}
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                  <div style={{ fontSize: 17, fontWeight: 700, color: paid ? "#065F46" : "#475569" }}>{fmt(wp.amount)}</div>
                  <button
                    type="button"
                    onClick={() => setWeeklyMenuId(menuOpen ? null : wp.id)}
                    style={{ background: "#EEF2FF", border: "1px solid #C7D2FE", borderRadius: 8, padding: "4px 8px", cursor: "pointer", color: "#4338CA", display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 700, fontFamily: FONT }}
                    title="Edit / mark / delete"
                  >
                    {Icons.edit} Edit
                  </button>
                </div>
              </div>
              {menuOpen && (
                <>
                  <div onClick={() => setWeeklyMenuId(null)} style={{ position: "fixed", inset: 0, zIndex: 50 }} />
                  <div style={{ position: "absolute", right: 12, top: 56, background: "white", border: "1px solid #E2E8F0", borderRadius: 10, boxShadow: "0 10px 30px rgba(15,23,42,0.18)", zIndex: 51, minWidth: 180, overflow: "hidden", fontFamily: FONT }}>
                    <button
                      type="button"
                      onClick={() => {
                        const today = new Date().toISOString().split("T")[0];
                        const next = paid ? "pending" : "paid";
                        const update = { id: wp.id, status: next };
                        if (next === "paid") {
                          update.paidDate = wp.paidDate || today;
                          if (!wp.paidByPersonId && investorOptions[0]) update.paidByPersonId = investorOptions[0].id;
                        }
                        updateWeeklyPayment(loc.id, update);
                        setWeeklyMenuId(null);
                      }}
                      style={{ width: "100%", textAlign: "left", padding: "10px 14px", background: "white", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600, color: paid ? "#92400E" : "#065F46", fontFamily: FONT }}
                    >
                      {paid ? "Mark as Pending" : "Mark as Paid"}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setWeeklyMenuId(null); setModal({ kind: "editWeeklyPayment", locationId: loc.id, weeklyPaymentId: wp.id }); }}
                      style={{ width: "100%", textAlign: "left", padding: "10px 14px", background: "white", border: "none", borderTop: "1px solid #F1F5F9", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#1E293B", fontFamily: FONT }}
                    >
                      Edit details
                    </button>
                    <button
                      type="button"
                      onClick={() => { setWeeklyMenuId(null); setModal({ kind: "deleteWeeklyPayment", locationId: loc.id, weeklyPaymentId: wp.id }); }}
                      style={{ width: "100%", textAlign: "left", padding: "10px 14px", background: "white", border: "none", borderTop: "1px solid #F1F5F9", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#991B1B", fontFamily: FONT }}
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })}
        {(loc.weeklyPayments || []).length === 0 && (
          <div style={{ background: "white", border: "1px dashed #E2E8F0", borderRadius: 12, padding: 24, textAlign: "center", fontSize: 14, color: "#94A3B8", fontFamily: FONT }}>
            No weekly payments yet. Tap Add to log one.
          </div>
        )}
      </div>
    );
  };

  const renderInvestorsTab = (loc) => (
    <div style={{ display: isWide ? "grid" : "block", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(360px, 1fr))" : undefined, gap: isWide ? 14 : 0 }}>
      {loc.participations.map(part => {
        const person = getPerson(people, part.personId);
        const color = colorForPerson(part.personId, people);
        const hasPct = part.percentage !== null && part.percentage !== undefined && part.percentage !== "";
        const shareAmount = hasPct ? Math.round(getLocationTotal(loc) * (part.percentage / 100)) : null;
        const paidAmount = getEffectivePaid(loc, part.personId);
        const balance = hasPct ? paidAmount - shareAmount : null;
        const canEditPart = isOwnerOrAdmin || part.personId === currentUserId;
        return (
          <div key={part.personId} style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderLeft: `4px solid ${color.accent}`, borderRadius: 14, padding: 18, marginBottom: isWide ? 0 : 12, fontFamily: FONT, boxShadow: SHADOW_CARD }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <button onClick={() => { setSelectedPersonId(part.personId); setScreen("personDetail"); }} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
                <Avatar personId={part.personId} size={42} people={people} />
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{personLabel(person, activeCompanyId)}</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: color.text, background: color.bg, padding: "3px 8px", borderRadius: 4, display: "inline-block", marginTop: 4 }}>
                    {hasPct ? `${part.percentage}% Ownership` : "% Not Set"}
                  </div>
                </div>
              </button>
              {canEditPart ? (
                <button onClick={() => setModal({ kind: "editParticipation", locationId: loc.id, personId: part.personId })} style={{ background: "#EEF2FF", border: "1px solid #C7D2FE", borderRadius: 8, padding: "6px 10px", cursor: "pointer", color: "#4338CA", display: "flex", alignItems: "center" }}>{Icons.edit}</button>
              ) : <LockedIndicator />}
            </div>
            {hasPct ? (
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)", gap: 10 }}>
                <div style={{ background: C.borderSoft, borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Paid</div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: C.text, letterSpacing: -0.5 }}>{fmt(paidAmount)}</div>
                </div>
                <div style={{ background: C.borderSoft, borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Share</div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: C.text, letterSpacing: -0.5 }}>{fmt(shareAmount)}</div>
                </div>
                <div style={{ background: balance >= 0 ? "#DCFCE7" : "#FEE2E2", borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ fontSize: 11, color: balance >= 0 ? "#065F46" : "#991B1B", fontWeight: 700, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Balance</div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: balance >= 0 ? "#065F46" : "#991B1B", letterSpacing: -0.5, display: "flex", alignItems: "center", gap: 2 }}>
                    {balance >= 0 ? Icons.arrowUp : Icons.arrowDown}{fmt(balance)}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 10 }}>
                <div style={{ background: C.borderSoft, borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Paid</div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: C.text, letterSpacing: -0.5 }}>{fmt(paidAmount)}</div>
                </div>
                <div style={{ background: "#FEF3C7", borderRadius: 10, padding: "12px 14px", display: "flex", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 11, color: "#92400E", fontWeight: 700, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>% Not Set</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#B45309" }}>Tap edit to assign</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderExpensesTab = (loc) => {
    const grouped = {};
    loc.expenses.forEach(e => {
      if (!grouped[e.paidByPersonId]) grouped[e.paidByPersonId] = [];
      grouped[e.paidByPersonId].push(e);
    });
    const sortedPayers = Object.entries(grouped)
      .map(([personId, items]) => ({ personId, items, total: items.reduce((s, e) => s + (Number(e.amount) || 0), 0) }))
      .sort((a, b) => b.total - a.total);
    const total = loc.expenses.reduce((s, e) => s + e.amount, 0);
    return (
      <div style={{ fontFamily: FONT }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: "#1E293B", margin: 0, fontFamily: FONT }}>Expenses</h3>
            <div style={{ fontSize: 12, color: "#94A3B8", fontFamily: FONT, marginTop: 2 }}>{loc.expenses.length} logged · {sortedPayers.length} payer{sortedPayers.length !== 1 ? "s" : ""}</div>
          </div>
          <button onClick={() => setModal({ kind: "addExpense", locationId: loc.id })} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: C.primaryGreen, color: C.white, border: "none", borderRadius: 10, padding: "10px 14px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>{Icons.plus} Add Expense</button>
        </div>
        {loc.expenses.length === 0 ? (
          <div style={{ background: C.cardBg, border: `1px dashed ${C.border}`, borderRadius: 14, padding: 48, textAlign: "center", boxShadow: SHADOW_CARD }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 4 }}>No expenses logged yet</div>
            <div style={{ fontSize: 14, color: C.mutedSoft, marginBottom: 16 }}>Track build-out costs, rent, machines, and other startup expenses.</div>
            <button onClick={() => setModal({ kind: "addExpense", locationId: loc.id })} style={{ background: C.primaryGreen, color: C.white, border: "none", borderRadius: 10, padding: "10px 16px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>Add First Expense</button>
          </div>
        ) : (
          <>
            {sortedPayers.map(({ personId, items, total: subtotal }) => {
              const color = colorForPerson(personId, people);
              const person = getPerson(people, personId);
              const isCurrentUser = personId === currentUserId;
              return (
                <div key={personId} style={{ background: "white", border: `1px solid ${color.border}40`, borderLeft: `4px solid ${color.accent}`, borderRadius: 12, marginBottom: 10, overflow: "hidden" }}>
                  <div style={{ padding: "12px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", background: color.bg + "30" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <Avatar personId={personId} size={32} people={people} />
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontSize: 16, fontWeight: 700, color: "#1E293B" }}>{cleanName(person.name)}</span>
                          {isCurrentUser && (<span style={{ fontSize: 10, fontWeight: 700, background: "#ECFDF5", color: "#065F46", padding: "2px 6px", borderRadius: 4 }}>YOU</span>)}
                        </div>
                        <div style={{ fontSize: 13, color: "#64748B" }}>{items.length} expense{items.length !== 1 ? "s" : ""}</div>
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 11, color: "#94A3B8", textTransform: "uppercase", fontWeight: 700 }}>Total</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: color.text }}>{fmt(subtotal)}</div>
                    </div>
                  </div>
                  <div>
                    {items.map((e, idx) => {
                      const canEdit = e.createdByPersonId === currentUserId || isOwner;
                      const linkedContractor = e.linkedContractorId
                        ? (loc.contractors || []).find(c => c.id === e.linkedContractorId)
                        : null;
                      const wasLoggedByOther = e.createdByPersonId !== e.paidByPersonId;
                      return (
                        <div key={e.id} style={{ padding: "10px 14px 10px 18px", borderBottom: idx < items.length - 1 ? "1px solid #F1F5F9" : "none" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2, flexWrap: "wrap" }}>
                                <span style={{ fontSize: 15, fontWeight: 600, color: "#1E293B" }}>{e.description}</span>
                                {linkedContractor && (
                                  <span style={{ fontSize: 11, fontWeight: 700, background: "#FFF7ED", color: "#9A3412", padding: "2px 7px", borderRadius: 4 }}>
                                    {linkedContractor.name}{linkedContractor.trade ? ` · ${linkedContractor.trade}` : ""}
                                  </span>
                                )}
                              </div>
                              {e.note && (
                                <div style={{ fontSize: 13, color: "#64748B", marginBottom: 2, fontStyle: "italic" }}>{e.note}</div>
                              )}
                              <div style={{ fontSize: 12, color: "#94A3B8" }}>{e.date}</div>
                              {wasLoggedByOther && (
                                <div style={{ marginTop: 6, display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: 6, fontSize: 13, color: "#94A3B8", fontStyle: "italic" }}>
                                  <span>Logged by</span>
                                  <AttributionBadge personId={e.createdByPersonId} people={people} currentUserId={currentUserId} />
                                </div>
                              )}
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
                              <span style={{ fontSize: 16, fontWeight: 700, color: "#065F46" }}>{fmt(e.amount)}</span>
                              {canEdit ? (
                                <EditDeleteButtons
                                  onEdit={() => setModal({ kind: "editExpense", locationId: loc.id, expenseId: e.id })}
                                  onDelete={() => setModal({ kind: "deleteExpense", locationId: loc.id, expenseId: e.id })}
                                />
                              ) : <LockedIndicator />}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ padding: "8px 14px", background: "#F8FAFC", display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #E2E8F0" }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#64748B" }}>{cleanName(person.name)} Total</span>
                    <span style={{ fontSize: 16, fontWeight: 700, color: color.text }}>{fmt(subtotal)}</span>
                  </div>
                </div>
              );
            })}
            <div style={{ background: "linear-gradient(135deg, #065F46 0%, #047857 100%)", padding: "14px 16px", borderRadius: 12, display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
              <div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.85)", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700 }}>Grand Total Expenses</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.65)" }}>Across {loc.expenses.length} entr{loc.expenses.length !== 1 ? "ies" : "y"}</div>
              </div>
              <span style={{ fontSize: 22, fontWeight: 700, color: "white", letterSpacing: -0.5 }}>{fmt(total)}</span>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderQuotesTab = (loc) => {
    if (selectedContractorId) {
      const c = (loc.contractors || []).find(c => c.id === selectedContractorId);
      if (!c) { setSelectedContractorId(null); return null; }
      const totalQuoted = c.quotes.reduce((s, q) => s + q.amount, 0);
      const totalPaid = c.payments.reduce((s, p) => s + p.amount, 0);
      const remainingRaw = totalQuoted - totalPaid;
      const remaining = Math.max(0, remainingRaw);
      const overpaid = remainingRaw < 0;
      const progressPct = totalQuoted > 0 ? Math.min(100, Math.round((totalPaid / totalQuoted) * 100)) : 0;
      const isPaid = remaining <= 0 && totalQuoted > 0;
      const statusInfo = isPaid
        ? { bg: "#ECFDF5", text: "#065F46", dot: "#10B981", label: "Paid in Full" }
        : totalPaid > 0
          ? { bg: "#FEF3C7", text: "#92400E", dot: "#F59E0B", label: "In Progress" }
          : { bg: "#F0F9FF", text: "#0369A1", dot: "#38BDF8", label: "Pending" };
      const initialQuote = c.quotes.find(q => q.type === "initial");
      const extraQuotes = c.quotes.filter(q => q.type === "extra");
      const cColor = colorForContractor(c.id, loc.contractors || []);
      const cGradient = `linear-gradient(135deg, ${cColor.accent}, ${cColor.border})`;
      const groupedPayments = {};
      c.payments.forEach(p => {
        if (!groupedPayments[p.paidByPersonId]) groupedPayments[p.paidByPersonId] = [];
        groupedPayments[p.paidByPersonId].push(p);
      });
      const sortedInvestors = Object.entries(groupedPayments)
        .map(([pid, pays]) => ({ personId: pid, pays, total: pays.reduce((s, p) => s + p.amount, 0) }))
        .sort((a, b) => b.total - a.total);
      return (
        <div>
          <button onClick={() => setSelectedContractorId(null)} style={{ background: "none", border: "none", color: "#4F46E5", display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600, cursor: "pointer", marginBottom: 12, padding: 0, fontFamily: FONT }}>
            {Icons.back} All Contractors
          </button>
          <div style={{ background: "white", border: `1px solid ${cColor.border}40`, borderLeft: `4px solid ${cColor.accent}`, borderRadius: 14, padding: 14, marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: cGradient, display: "flex", alignItems: "center", justifyContent: "center", color: "white" }}>{Icons.hammer}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 17, fontWeight: 700, color: "#1E293B" }}>{c.name}</div>
                  <div style={{ fontSize: 13, color: "#64748B" }}>{c.trade}</div>
                  {c.phone && (
                    <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4, color: "#64748B" }}>
                      {Icons.phone}
                      <span style={{ fontSize: 13 }}>{c.phone}</span>
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4, background: statusInfo.bg, color: statusInfo.text, padding: "4px 10px", borderRadius: 8, fontSize: 12, fontWeight: 700 }}>
                  <div style={{ width: 5, height: 5, borderRadius: "50%", background: statusInfo.dot }} />
                  {statusInfo.label}
                </div>
                <button onClick={() => setModal({ kind: "editContractor", locationId: loc.id, contractorId: c.id })} title="Edit contractor" style={{ background: "#EEF2FF", border: "1px solid #C7D2FE", borderRadius: 8, padding: "4px 7px", cursor: "pointer", color: "#4338CA", display: "flex", alignItems: "center" }}>{Icons.edit}</button>
                <button onClick={() => setModal({ kind: "deleteContractor", locationId: loc.id, contractorId: c.id })} title="Delete contractor" style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "4px 7px", cursor: "pointer", color: "#991B1B", display: "flex", alignItems: "center" }}>{Icons.trash}</button>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)", gap: 8, marginBottom: 12 }}>
              <div style={{ background: "#F0F1F5", borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ fontSize: 11, color: "#475569", fontWeight: 700, marginBottom: 3, textTransform: "uppercase" }}>Quoted</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#1E293B" }}>{fmt(totalQuoted)}</div>
              </div>
              <div style={{ background: "#ECFDF5", borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ fontSize: 11, color: "#065F46", fontWeight: 700, marginBottom: 3, textTransform: "uppercase" }}>Paid</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#065F46" }}>{fmt(totalPaid)}</div>
              </div>
              <div style={{ background: (isPaid && !overpaid) ? "#ECFDF5" : "#FEE2E2", borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ fontSize: 11, color: (isPaid && !overpaid) ? "#065F46" : "#991B1B", fontWeight: 700, marginBottom: 3, textTransform: "uppercase" }}>{overpaid ? "Overpaid" : "Remaining"}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: (isPaid && !overpaid) ? "#065F46" : "#DC2626" }}>{overpaid ? "-" + fmt(remainingRaw) : fmt(remaining)}</div>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>Payment Progress</span>
              <span style={{ fontSize: 12, color: isPaid ? "#065F46" : "#4F46E5", fontWeight: 700 }}>{progressPct}%</span>
            </div>
            <div style={{ height: 8, borderRadius: 4, background: "#E2E8F0", overflow: "hidden" }}>
              <div style={{ height: "100%", borderRadius: 4, width: progressPct + "%", background: isPaid ? "#10B981" : cGradient }} />
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: "#1E293B", margin: 0, fontFamily: FONT }}>Quote Breakdown</h3>
              <div style={{ fontSize: 12, color: "#94A3B8", fontFamily: FONT, marginTop: 2 }}>
                {c.quotes.length} {c.quotes.length === 1 ? "item" : "items"} {String.fromCharCode(183)} {extraQuotes.length} extra
              </div>
            </div>
            <button onClick={() => setModal({ kind: "addQuote", locationId: loc.id, contractorId: c.id, hasInitial: !!initialQuote })} style={{ display: "flex", alignItems: "center", gap: 4, background: "#F97316", color: "white", border: "none", borderRadius: 10, padding: "8px 12px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>
              {Icons.plus} {initialQuote ? "Add Extra" : "Add Quote"}
            </button>
          </div>
          <div style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 14, overflow: "hidden", marginBottom: 16 }}>
            {initialQuote && (() => {
              const canEdit = initialQuote.createdByPersonId === currentUserId || isOwner;
              return (
                <div style={{ background: "linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)", padding: 14, borderBottom: extraQuotes.length > 0 ? "1px solid #FED7AA" : "none" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: "#9A3412", background: "#FED7AA", padding: "3px 8px", borderRadius: 4, marginBottom: 6, textTransform: "uppercase" }}>
                        Initial Quote
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: "#1E293B", marginBottom: 2 }}>{initialQuote.description}</div>
                      <div style={{ fontSize: 12, color: "#9A3412", fontWeight: 500 }}>Agreed {initialQuote.date}</div>
                    </div>
                    <div style={{ textAlign: "right", marginLeft: 10 }}>
                      <div style={{ fontSize: 11, color: "#9A3412", textTransform: "uppercase", fontWeight: 700, marginBottom: 2 }}>Base</div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: "#1E293B", letterSpacing: -0.5 }}>{fmt(initialQuote.amount)}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                    <AttributionBadge personId={initialQuote.createdByPersonId} people={people} currentUserId={currentUserId} />
                    {canEdit ? (
                      <EditDeleteButtons
                        onEdit={() => setModal({ kind: "editQuote", locationId: loc.id, contractorId: c.id, quoteId: initialQuote.id })}
                        onDelete={() => setModal({ kind: "deleteQuote", locationId: loc.id, contractorId: c.id, quoteId: initialQuote.id })}
                      />
                    ) : <LockedIndicator />}
                  </div>
                </div>
              );
            })()}
            {extraQuotes.length > 0 && (
              <div style={{ background: "#FAFBFC", padding: "10px 0" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", padding: "0 14px 8px" }}>
                  Extra Work Added Later ({extraQuotes.length})
                </div>
                {extraQuotes.map((q, qi) => {
                  const canEdit = q.createdByPersonId === currentUserId || isOwner;
                  const isLast = qi === extraQuotes.length - 1;
                  return (
                    <div key={q.id} style={{ position: "relative", padding: "10px 16px 10px 32px", borderTop: qi > 0 ? "1px solid #F1F5F9" : "none" }}>
                      <div style={{ position: "absolute", left: 22, top: 14, width: 8, height: 8, borderRadius: "50%", background: "#F97316", border: "2px solid white", boxShadow: "0 0 0 1px #F97316" }} />
                      {!isLast && (<div style={{ position: "absolute", left: 25.5, top: 22, bottom: -8, width: 1, background: "#FED7AA" }} />)}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: "#9A3412", background: "#FED7AA", padding: "2px 7px", borderRadius: 3, textTransform: "uppercase" }}>Extra</span>
                            <span style={{ fontSize: 12, color: "#94A3B8" }}>{q.date}</span>
                          </div>
                          <div style={{ fontSize: 15, fontWeight: 600, color: "#1E293B", lineHeight: 1.3 }}>{q.description}</div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 3, marginLeft: 8 }}>
                          <span style={{ fontSize: 14, fontWeight: 600, color: "#F97316" }}>+</span>
                          <span style={{ fontSize: 17, fontWeight: 700, color: "#1E293B" }}>{fmt(q.amount)}</span>
                        </div>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                        <AttributionBadge personId={q.createdByPersonId} people={people} currentUserId={currentUserId} />
                        {canEdit ? (
                          <EditDeleteButtons
                            onEdit={() => setModal({ kind: "editQuote", locationId: loc.id, contractorId: c.id, quoteId: q.id })}
                            onDelete={() => setModal({ kind: "deleteQuote", locationId: loc.id, contractorId: c.id, quoteId: q.id })}
                          />
                        ) : <LockedIndicator />}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ background: "linear-gradient(135deg, #1E293B 0%, #334155 100%)", padding: "14px 16px", color: "white" }}>
              {extraQuotes.length > 0 && initialQuote && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13, opacity: 0.85, marginBottom: 4 }}>
                  <span>{fmt(initialQuote.amount)} base + {fmt(extraQuotes.reduce((s, q) => s + q.amount, 0))} extras</span>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 12, opacity: 0.85, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700 }}>Total Quoted</div>
                  <div style={{ fontSize: 11, opacity: 0.65, marginTop: 1 }}>{c.name} {String.fromCharCode(183)} all work</div>
                </div>
                <span style={{ fontSize: 24, fontWeight: 700, letterSpacing: -0.5 }}>{fmt(totalQuoted)}</span>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: "#1E293B", margin: 0, fontFamily: FONT }}>Payments</h3>
              <div style={{ fontSize: 12, color: "#94A3B8", fontFamily: FONT, marginTop: 2 }}>{c.payments.length} payment{c.payments.length !== 1 ? "s" : ""}</div>
            </div>
          </div>
          {c.payments.length === 0 ? (
            <div style={{ background: "white", border: "1px dashed #CBD5E1", borderRadius: 12, padding: 24, textAlign: "center" }}>
              <div style={{ fontSize: 14, color: "#94A3B8" }}>No payments logged yet</div>
            </div>
          ) : (
            <>
              {sortedInvestors.map(({ personId, pays, total }) => {
                const color = colorForPerson(personId, people);
                const person = getPerson(people, personId);
                const isCurrentUser = personId === currentUserId;
                return (
                  <div key={personId} style={{ background: "white", border: "1px solid " + color.border + "40", borderLeft: "4px solid " + color.accent, borderRadius: 12, marginBottom: 10, overflow: "hidden" }}>
                    <div style={{ padding: "12px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", background: color.bg + "30" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <Avatar personId={personId} size={32} people={people} />
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontSize: 16, fontWeight: 700, color: "#1E293B" }}>{cleanName(person.name)}</span>
                            {isCurrentUser && (<span style={{ fontSize: 10, fontWeight: 700, background: "#ECFDF5", color: "#065F46", padding: "2px 6px", borderRadius: 4 }}>YOU</span>)}
                          </div>
                          <div style={{ fontSize: 13, color: "#64748B" }}>{pays.length} payment{pays.length !== 1 ? "s" : ""}</div>
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 11, color: "#94A3B8", textTransform: "uppercase", fontWeight: 700 }}>Total</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: color.text }}>{fmt(total)}</div>
                      </div>
                    </div>
                    <div>
                      {pays.map((p, idx) => {
                        const wasLoggedByOther = p.createdByPersonId !== p.paidByPersonId;
                        const creator = getPerson(people, p.createdByPersonId);
                        return (
                          <div key={p.id} style={{ padding: "10px 14px 10px 18px", borderBottom: idx < pays.length - 1 ? "1px solid #F1F5F9" : "none" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                                  <span style={{ fontSize: 15, fontWeight: 600, color: "#1E293B" }}>Payment {idx + 1}</span>
                                  <span style={{ fontSize: 11, fontWeight: 600, background: "#F1F5F9", color: "#64748B", padding: "2px 7px", borderRadius: 3 }}>{p.method}</span>
                                </div>
                                <div style={{ fontSize: 12, color: "#94A3B8" }}>{p.date}</div>
                              </div>
                              <span style={{ fontSize: 16, fontWeight: 700, color: "#065F46", display: "flex", alignItems: "center", gap: 3 }}>
                                {Icons.check}
                                {fmt(p.amount)}
                              </span>
                            </div>
                            {wasLoggedByOther && (
                              <div style={{ marginTop: 4, paddingTop: 4, borderTop: "1px dashed #E2E8F0" }}>
                                <div style={{ fontSize: 11, color: "#94A3B8", fontStyle: "italic" }}>Logged by {creator.name}</div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ padding: "8px 14px", background: "#F8FAFC", display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #E2E8F0" }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#64748B" }}>{cleanName(person.name)} Total</span>
                      <span style={{ fontSize: 16, fontWeight: 700, color: color.text }}>{fmt(total)}</span>
                    </div>
                  </div>
                );
              })}
              <div style={{ background: "linear-gradient(135deg, #065F46 0%, #047857 100%)", padding: "14px 16px", borderRadius: 12, display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
                <div>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.85)", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700 }}>Grand Total Paid</div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.65)" }}>Across {c.payments.length} payment{c.payments.length !== 1 ? "s" : ""}</div>
                </div>
                <span style={{ fontSize: 22, fontWeight: 700, color: "white", letterSpacing: -0.5 }}>{fmt(totalPaid)}</span>
              </div>
            </>
          )}
        </div>
      );
    }
    const contractors = loc.contractors || [];
    return (
      <div style={{ fontFamily: FONT }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: "#1E293B", margin: 0 }}>Contractors ({contractors.length})</h3>
          <button onClick={() => setModal({ kind: "addContractor", locationId: loc.id })} style={{ display: "flex", alignItems: "center", gap: 4, background: "#F97316", color: "white", border: "none", borderRadius: 10, padding: "8px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            {Icons.plus} New Contractor
          </button>
        </div>
        {contractors.length === 0 ? (
          <div style={{ background: "white", border: "1px dashed #CBD5E1", borderRadius: 14, padding: 30, textAlign: "center" }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 8, color: "#CBD5E1" }}>{Icons.hammer}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#94A3B8", marginBottom: 4 }}>No contractors yet</div>
            <div style={{ fontSize: 11, color: "#CBD5E1" }}>Add contractors to track quotes and payments</div>
          </div>
        ) : (
          contractors.map((c) => {
            const totalQuoted = c.quotes.reduce((s, q) => s + q.amount, 0);
            const totalPaid = c.payments.reduce((s, p) => s + p.amount, 0);
            const remainingRaw = totalQuoted - totalPaid;
            const remaining = Math.max(0, remainingRaw);
            const overpaid = remainingRaw < 0;
            const progressPct = totalQuoted > 0 ? Math.min(100, Math.round((totalPaid / totalQuoted) * 100)) : 0;
            const isPaid = remaining <= 0 && totalQuoted > 0;
            const hasExtras = c.quotes.filter(q => q.type === "extra").length > 0;
            const statusInfo = isPaid
              ? { bg: "#ECFDF5", text: "#065F46", dot: "#10B981", label: "Paid" }
              : totalPaid > 0
                ? { bg: "#FEF3C7", text: "#92400E", dot: "#F59E0B", label: "In Progress" }
                : { bg: "#F0F9FF", text: "#0369A1", dot: "#38BDF8", label: "Pending" };
            const cColor = colorForContractor(c.id, contractors);
            const cGradient = `linear-gradient(135deg, ${cColor.accent}, ${cColor.border})`;
            return (
              <button key={c.id} onClick={() => setSelectedContractorId(c.id)} style={{ width: "100%", background: "white", border: `1px solid ${cColor.border}40`, borderLeft: `4px solid ${cColor.accent}`, borderRadius: 12, padding: 12, marginBottom: 8, textAlign: "left", cursor: "pointer", fontFamily: FONT }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1 }}>
                    <div style={{ width: 38, height: 38, borderRadius: 10, background: cGradient, display: "flex", alignItems: "center", justifyContent: "center", color: "white" }}>{Icons.hammer}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#1E293B" }}>{c.name}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 1 }}>
                        <span style={{ fontSize: 11, color: "#64748B" }}>{c.trade}</span>
                        {hasExtras && (<span style={{ fontSize: 9, fontWeight: 700, background: cColor.bg, color: cColor.text, padding: "1px 5px", borderRadius: 3 }}>+ EXTRAS</span>)}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 4, background: statusInfo.bg, color: statusInfo.text, padding: "3px 8px", borderRadius: 8, fontSize: 10, fontWeight: 700 }}>
                    <div style={{ width: 5, height: 5, borderRadius: "50%", background: statusInfo.dot }} />
                    {statusInfo.label}
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)", gap: 6, marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 9, color: "#94A3B8", marginBottom: 1 }}>Quoted</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#1E293B" }}>{fmt(totalQuoted)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 9, color: "#94A3B8", marginBottom: 1 }}>Paid</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#065F46" }}>{fmt(totalPaid)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 9, color: "#94A3B8", marginBottom: 1 }}>{overpaid ? "Overpaid" : "Remaining"}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: (isPaid && !overpaid) ? "#065F46" : "#DC2626" }}>{overpaid ? "-" + fmt(remainingRaw) : fmt(remaining)}</div>
                  </div>
                </div>
                <div style={{ height: 5, borderRadius: 3, background: "#F1F5F9", overflow: "hidden" }}>
                  <div style={{ height: "100%", borderRadius: 3, width: progressPct + "%", background: isPaid ? "#10B981" : `linear-gradient(90deg, ${cColor.accent}, ${cColor.border})` }} />
                </div>
              </button>
            );
          })
        )}
      </div>
    );
  };

  const renderPaymentsTab = (loc) => {
    // Investor-to-investor payments only. Contractor payments live on
    // each contractor card under the Contractors tab — surfacing them
    // here mixed with investor payments was confusing.
    const debts = computeDebts(loc);
    const allPayments = (loc.payments || [])
      .map(p => ({ ...p, kind: "investor" }))
      .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    return (
      <div>
        {debts.length > 0 && (
          <>
            <h3 style={{ fontSize: 12, fontWeight: 700, color: C.muted, margin: "0 0 12px", textTransform: "uppercase", letterSpacing: 0.8, fontFamily: FONT }}>Outstanding Balances</h3>
            {debts.map((d, i) => {
              const fromColor = colorForPerson(d.fromPersonId, people);
              const toColor = colorForPerson(d.toPersonId, people);
              const fromPerson = getPerson(people, d.fromPersonId);
              const toPerson = getPerson(people, d.toPersonId);
              const isComplete = d.remaining <= 0;
              const canPay = d.fromPersonId === currentUserId || isOwnerOrAdmin;
              return (
                <div key={i} style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, marginBottom: 10, fontFamily: FONT, boxShadow: SHADOW_CARD }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                    <Avatar personId={d.fromPersonId} size={32} people={people} />
                    <span style={{ fontSize: 16, color: C.mutedSoft }}>{String.fromCharCode(8594)}</span>
                    <Avatar personId={d.toPersonId} size={32} people={people} />
                    <div style={{ flex: 1, marginLeft: 6 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
                        <span style={{ color: fromColor.text }}>{cleanName(fromPerson.name)}</span> owes <span style={{ color: toColor.text }}>{cleanName(toPerson.name)}</span>
                      </div>
                      <div style={{ fontSize: 12, color: C.mutedSoft, marginTop: 2 }}>Outstanding</div>
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: isComplete ? C.accentGreen : C.deleteRed, letterSpacing: -0.5 }}>{fmt(d.remaining)}</div>
                  </div>
                  {d.pending > 0 && <div style={{ fontSize: 12, color: "#92400E", background: "#FEF3C7", padding: "6px 12px", borderRadius: 8, marginBottom: 10, fontWeight: 600 }}>{fmt(d.pending)} pending confirmation</div>}
                  {!isComplete && canPay && (
                    <button onClick={() => setModal({ kind: "addPayment", locationId: loc.id, fromPersonId: d.fromPersonId, toPersonId: d.toPersonId, suggestedAmount: d.remaining })} style={{ width: "100%", background: C.primaryGreen, color: C.white, border: "none", borderRadius: 8, padding: "10px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>Log Payment</button>
                  )}
                </div>
              );
            })}
          </>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "20px 0 12px" }}>
          <h3 style={{ fontSize: 12, fontWeight: 700, color: C.muted, margin: 0, textTransform: "uppercase", letterSpacing: 0.8, fontFamily: FONT }}>Payment History</h3>
          {isOwnerOrAdmin && (
            <button onClick={() => setModal({ kind: "addPayment", locationId: loc.id, onBehalf: true })} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: C.primaryGreen, color: C.white, border: "none", borderRadius: 10, padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>{Icons.plus} Log Payment</button>
          )}
        </div>
        {allPayments.map(p => {
          const isContractor = p.kind === "contractor";
          const fromColor = colorForPerson(p.fromPersonId, people);
          const fromPerson = getPerson(people, p.fromPersonId);
          const toLabel = isContractor
            ? (p.contractorTrade ? `${p.contractorName} (${p.contractorTrade})` : p.contractorName)
            : cleanName(getPerson(people, p.toPersonId).name);
          // Only the receiver of the payment can confirm/reject — owners/admins
          // can still see and delete pending payments, but the accept/reject
          // call belongs to the person who's supposed to have received the money.
          const canConfirm = !isContractor && p.status === "pending" && p.toPersonId === currentUserId;
          const onBehalf = !isContractor && p.onBehalfOfPersonId && p.createdByPersonId !== p.fromPersonId;
          const canDeletePayment = isOwnerOrAdmin || p.createdByPersonId === currentUserId || p.fromPersonId === currentUserId;
          const deleteModalKind = isContractor ? "deleteContractorPaymentConfirm" : "deleteInvestorPaymentConfirm";
          return (
            <div key={`${p.kind}-${p.id}`} style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderLeft: `3px solid ${fromColor.accent}`, borderRadius: 12, padding: 14, marginBottom: 10, fontFamily: FONT, boxShadow: SHADOW_CARD }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 4 }}>
                    <span style={{ color: fromColor.text }}>{cleanName(fromPerson.name)}</span> {String.fromCharCode(8594)} <span>{toLabel}</span>
                    {isContractor && <span style={{ marginLeft: 8, fontSize: 9, fontWeight: 700, background: "#FEF3C7", color: "#92400E", padding: "2px 6px", borderRadius: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Contractor</span>}
                  </div>
                  <div style={{ fontSize: 12, color: C.mutedSoft }}>{p.source} {String.fromCharCode(183)} {p.date}</div>
                  {onBehalf && (
                    <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: C.muted }}>
                      Logged by <AttributionBadge personId={p.createdByPersonId} people={people} currentUserId={currentUserId} />
                    </div>
                  )}
                  {p.note && <div style={{ fontSize: 12, color: C.muted, marginTop: 6, fontStyle: "italic" }}>"{p.note}"</div>}
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, marginLeft: 12, flexShrink: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: fromColor.text, letterSpacing: -0.5 }}>{fmt(p.amount)}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 4, background: p.status === "confirmed" ? "#ECFDF5" : p.status === "pending" ? "#FEF3C7" : "#FEE2E2", color: p.status === "confirmed" ? "#065F46" : p.status === "pending" ? "#92400E" : "#991B1B", textTransform: "uppercase", letterSpacing: 0.5 }}>{p.status}</div>
                    {canDeletePayment && !canConfirm && (
                      <button
                        onClick={() => setModal(isContractor
                          ? { kind: deleteModalKind, locationId: loc.id, contractorId: p.contractorId, paymentId: p.id }
                          : { kind: deleteModalKind, locationId: loc.id, paymentId: p.id })}
                        title="Delete payment"
                        style={{ background: "#FEF2F2", color: "#991B1B", border: "1px solid #FECACA", borderRadius: 6, padding: "3px 6px", cursor: "pointer", fontFamily: FONT, display: "inline-flex", alignItems: "center" }}
                      >
                        {Icons.trash}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {canConfirm && (
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <button onClick={() => confirmPayment(loc.id, p.id)} style={{ flex: 1, background: C.accentGreen, color: C.white, border: "none", borderRadius: 8, padding: "10px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>Confirm Received</button>
                  <button onClick={() => rejectPayment(loc.id, p.id)} style={{ flex: 1, background: "#FEE2E2", color: "#991B1B", border: "1px solid #FCA5A5", borderRadius: 8, padding: "10px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>Reject</button>
                  {canDeletePayment && (
                    <button
                      onClick={() => setModal(isContractor
                        ? { kind: deleteModalKind, locationId: loc.id, contractorId: p.contractorId, paymentId: p.id }
                        : { kind: deleteModalKind, locationId: loc.id, paymentId: p.id })}
                      title="Delete payment"
                      style={{ background: "#FEF2F2", color: "#991B1B", border: "1px solid #FECACA", borderRadius: 8, padding: "8px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: FONT, display: "inline-flex", alignItems: "center" }}
                    >
                      {Icons.trash}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {allPayments.length === 0 && (
          <div style={{ background: C.cardBg, border: `1px dashed ${C.border}`, borderRadius: 14, padding: 40, textAlign: "center", boxShadow: SHADOW_CARD }}>
            <div style={{ fontSize: 14, color: C.mutedSoft, fontWeight: 600 }}>No payments logged yet</div>
          </div>
        )}
      </div>
    );
  };

  const renderModal = () => {
    if (!modal) return null;
    if (modal.kind === "deleteLocation") {
      const loc = locations.find(l => l.id === modal.locationId);
      return (
        <ModalShell onClose={closeModal}>
          <ModalHeader title="Remove Location" onClose={closeModal} />
          <div style={{ fontSize: 13, color: "#475569", marginBottom: 16, lineHeight: 1.5 }}>Permanently delete <strong>{loc?.name}</strong> and all its data? This cannot be undone.</div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={closeModal} style={{ flex: 1, padding: "12px", background: "#F1F5F9", color: "#475569", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>Cancel</button>
            <button onClick={() => { removeLocation(modal.locationId); setScreen("list"); closeModal(); }} style={{ flex: 1, padding: "12px", background: "#DC2626", color: "white", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>Delete</button>
          </div>
        </ModalShell>
      );
    }
    if (modal.kind === "addExpense" || modal.kind === "editExpense") return <ExpenseModal modal={modal} onClose={closeModal} people={companyPeople} locations={locations} addExpense={addExpense} updateExpense={updateExpense} addContractorPayment={addContractorPayment} activeCompanyId={activeCompanyId} expenseDescriptions={expenseDescriptions} isAdmin={isOwnerOrAdmin} currentUserId={currentUserId} />;
    if (modal.kind === "addPayment" || modal.kind === "editPayment") return <PaymentModal modal={modal} onClose={closeModal} people={people} locations={locations} addPayment={addPayment} isOwner={isOwnerOrAdmin} currentUserId={currentUserId} activeCompanyId={activeCompanyId} />;
    if (modal.kind === "addLocation" || modal.kind === "editLocation") return <LocationModal modal={modal} onClose={closeModal} locations={locations} people={companyPeople} saveLocation={saveLocation} activeCompanyId={activeCompanyId} />;
    if (modal.kind === "editParticipation") return <ParticipationModal modal={modal} onClose={closeModal} people={people} locations={locations} updateParticipation={updateParticipationLocal} />;
    if (modal.kind === "addContractor" || modal.kind === "editContractor") return <ContractorModal modal={modal} onClose={closeModal} addContractor={addContractor} locations={locations} savedContractors={savedContractors.filter(s => !activeCompanyId || s.companyId === activeCompanyId)} contractorCategories={contractorCategories} addContractorCategory={addContractorCategory} />;
    if (modal.kind === "deleteContractor") return <DeleteContractorModal modal={modal} onClose={closeModal} locations={locations} removeContractor={removeContractor} onAfterDelete={() => setSelectedContractorId(null)} />;
    if (modal.kind === "deleteExpense") {
      const _loc = locations.find(l => l.id === modal.locationId);
      const _exp = (_loc?.expenses || []).find(e => e.id === modal.expenseId);
      if (!_exp) { closeModal(); return null; }
      const _linkedContractor = _exp.linkedContractorId
        ? (_loc?.contractors || []).find(c => c.id === _exp.linkedContractorId)
        : null;
      return (
        <ConfirmDeleteModal
          title="Delete Expense"
          message={
            <>
              Delete <b>{_exp.description}</b> ({fmt(_exp.amount)})? This will reduce the payer's Paid total by this amount.
              {_linkedContractor && <><br /><br />The matching payment on <b>{_linkedContractor.name}</b> will also be removed, increasing their Remaining balance by {fmt(_exp.amount)}.</>}
            </>
          }
          onClose={closeModal}
          onConfirm={() => removeExpense(modal.locationId, modal.expenseId)}
        />
      );
    }
    if (modal.kind === "deleteQuote") {
      const _loc = locations.find(l => l.id === modal.locationId);
      const _c = (_loc?.contractors || []).find(c => c.id === modal.contractorId);
      const _q = (_c?.quotes || []).find(q => q.id === modal.quoteId);
      if (!_q) { closeModal(); return null; }
      return (
        <ConfirmDeleteModal
          title="Delete Quote"
          message={<>Delete <b>{_q.description}</b> ({fmt(_q.amount)}) from {_c?.name || "this contractor"}? Payments already logged are not affected.</>}
          onClose={closeModal}
          onConfirm={() => removeQuote(modal.locationId, modal.contractorId, modal.quoteId)}
        />
      );
    }
    if (modal.kind === "deleteInvestorPaymentConfirm") {
      const _loc = locations.find(l => l.id === modal.locationId);
      const _p = (_loc?.payments || []).find(x => x.id === modal.paymentId);
      if (!_p) { closeModal(); return null; }
      const _from = getPerson(people, _p.fromPersonId);
      const _to = getPerson(people, _p.toPersonId);
      const wasConfirmed = _p.status === "confirmed";
      return (
        <ConfirmDeleteModal
          title="Delete Payment"
          message={<>Delete payment of {fmt(_p.amount)} from <b>{cleanName(_from.name)}</b> to <b>{cleanName(_to.name)}</b>?{wasConfirmed ? " This payment was confirmed, so removing it will shift both balances back." : ""}</>}
          onClose={closeModal}
          onConfirm={() => removePayment(modal.locationId, modal.paymentId)}
        />
      );
    }
    if (modal.kind === "deleteContractorPaymentConfirm") {
      const _loc = locations.find(l => l.id === modal.locationId);
      const _c = (_loc?.contractors || []).find(c => c.id === modal.contractorId);
      const _p = (_c?.payments || []).find(x => x.id === modal.paymentId);
      if (!_p) { closeModal(); return null; }
      const _from = getPerson(people, _p.paidByPersonId);
      return (
        <ConfirmDeleteModal
          title="Delete Contractor Payment"
          message={<>Delete payment of {fmt(_p.amount)} from <b>{cleanName(_from.name)}</b> to <b>{_c?.name || "contractor"}</b>? This will increase the contractor's Remaining balance by this amount. The original expense entry stays unless you also delete it from the Expenses tab.</>}
          onClose={closeModal}
          onConfirm={() => removeContractorPayment(modal.locationId, modal.contractorId, modal.paymentId)}
        />
      );
    }
    if (modal.kind === "addQuote" || modal.kind === "editQuote") return <QuoteModal modal={modal} onClose={closeModal} locations={locations} addQuote={addQuote} />;
    if (modal.kind === "addContractorPayment" || modal.kind === "editContractorPayment") return <ContractorPaymentModal modal={modal} onClose={closeModal} locations={locations} people={companyPeople} addContractorPayment={addContractorPayment} activeCompanyId={activeCompanyId} />;
    if (modal.kind === "addWeeklyPayment" || modal.kind === "editWeeklyPayment") return <WeeklyPaymentModal modal={modal} onClose={closeModal} locations={locations} people={people} addWeeklyPayment={addWeeklyPayment} updateWeeklyPayment={updateWeeklyPayment} activeCompanyId={activeCompanyId} currentUserId={currentUserId} />;
    if (modal.kind === "deleteWeeklyPayment") {
      const _loc = locations.find(l => l.id === modal.locationId);
      const _wp = (_loc?.weeklyPayments || []).find(w => w.id === modal.weeklyPaymentId);
      if (!_wp) { closeModal(); return null; }
      return (
        <ConfirmDeleteModal
          title="Delete Weekly Payment"
          message={<>Delete weekly payment of <b>{fmt(_wp.amount)}</b> for <b>{_wp.week || "this week"}</b>? This cannot be undone.</>}
          onClose={closeModal}
          onConfirm={() => removeWeeklyPayment(modal.locationId, modal.weeklyPaymentId)}
        />
      );
    }
    if (modal.kind === "userSwitcher") return <UserSwitcherModal onClose={closeModal} people={companyPeople} realUserId={realUserId} viewAsPersonId={viewAsPersonId} switchUser={(id) => { switchUser(id); closeModal(); }} />;
    if (modal.kind === "expenseDescriptionsSettings") return <ExpenseDescriptionsModal onClose={closeModal} descriptions={expenseDescriptions} onChange={updateExpenseDescriptions} />;
    if (modal.kind === "savedContractors") return <SavedContractorsModal onClose={closeModal} savedContractors={savedContractors.filter(s => !activeCompanyId || s.companyId === activeCompanyId)} locations={companyLocations} upsert={upsertSavedContractor} remove={removeSavedContractorEntry} contractorCategories={contractorCategories} addContractorCategory={addContractorCategory} />;
    return null;
  };

  // Settings screen — investor management (add/edit/remove people)
  // Toolbar — matches the BeeNexa Loans toolbar pattern. Detail screens
  // surface a Back button as the first header action; list view exposes
  // a New Location button. Investor lifecycle is managed via Manage
  // Employee (assign ROLE_INVESTOR_TRACKER), so there is no in-module
  // settings shortcut.
  const renderToolbar = () => {
    const inDetail = screen !== "list";
    const headerButtons = [];
    if (inDetail) {
      headerButtons.push({
        icon: <KeyboardBackspaceIcon />,
        title: "Back",
        handler: () => {
          setScreen("list");
          setSelectedLocationId(null);
          setSelectedPersonId(null);
          setSelectedContractorId(null);
        },
      });
    } else {
      headerButtons.push({
        icon: <AddCircleIcon />,
        title: "New Location",
        handler: () => setModal({ kind: "addLocation" }),
      });
    }
    let title = "Investor Tracker";
    if (screen === "locationDetail") {
      const loc = locations.find(l => l.id === selectedLocationId);
      title = loc?.name || "Location";
    } else if (screen === "personDetail") {
      const person = people.find(p => p.id === selectedPersonId);
      title = person?.name || "Investor";
    }
    return (
      <CustomToolbar
        title={title}
        createTitle="Investor Tracker"
        backTitle="Back to Investor Tracker"
        onClick={() => {}}
        headerButtons={headerButtons}
      />
    );
  };

  if (userCompanies.length === 0 && companies.length === 0) {
    return (
      <>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
        {renderToolbar()}
        <div style={{ width: "100%", background: C.bg, minHeight: "100vh", fontFamily: FONT, padding: 30, textAlign: "center" }}>
          <div style={{ marginTop: 60 }}>
            <div style={{ fontSize: 48, color: "#CBD5E1", marginBottom: 16 }}>{Icons.briefcase}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#1E293B", marginBottom: 8 }}>No data yet</div>
            <div style={{ fontSize: 13, color: "#64748B" }}>Use the + button in the toolbar to create your first location.</div>
          </div>
          {renderModal()}
        </div>
      </>
    );
  }

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      {renderToolbar()}
      <div style={{ width: "100%", maxWidth: "100%", overflowX: "clip", background: C.bg, minHeight: "calc(100vh - 64px)", fontFamily: FONT }}>
        {screen === "list" && renderLocationsList()}
        {screen === "locationDetail" && renderLocationDetail()}
        {screen === "personDetail" && renderPersonDetail()}
        {renderModal()}
      </div>
    </>
  );
}

function UserSwitcherModal({ onClose, people, realUserId, viewAsPersonId, switchUser }) {
  const sorted = [...people].sort((a, b) => {
    if (a.id === realUserId) return -1;
    if (b.id === realUserId) return 1;
    return cleanName(a.name).localeCompare(cleanName(b.name));
  });
  const activeId = viewAsPersonId || realUserId;
  return (
    <ModalShell onClose={onClose}>
      <ModalHeader title="Switch User" onClose={onClose} />
      <div style={{ fontSize: 12, color: "#64748B", marginBottom: 12 }}>For testing role-based views</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {sorted.map((p) => {
          const isActive = p.id === activeId;
          const isReal = p.id === realUserId;
          const color = colorForPerson(p.id, people);
          return (
            <button
              key={p.id}
              onClick={() => switchUser(p.id)}
              style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "10px 12px",
                background: isActive ? "#F0FDF4" : "#FFFFFF",
                border: `1px solid ${isActive ? "#10B981" : "#E5E7EB"}`,
                borderRadius: 10, cursor: "pointer", fontFamily: FONT, textAlign: "left",
              }}
            >
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: color.accent, color: "white", fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {cleanName(p.name).charAt(0)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#1E293B" }}>{cleanName(p.name)}</span>
                  {isReal && <span style={{ fontSize: 9, fontWeight: 700, background: "#ECFDF5", color: "#065F46", padding: "2px 6px", borderRadius: 4 }}>YOU</span>}
                  {isActive && !isReal && <span style={{ fontSize: 9, fontWeight: 700, background: "#FEF3C7", color: "#92400E", padding: "2px 6px", borderRadius: 4 }}>PREVIEWING</span>}
                </div>
                <div style={{ fontSize: 11, color: "#64748B", marginTop: 2, textTransform: "capitalize" }}>{(p.role || "investor").replace(/_/g, " ")}</div>
              </div>
            </button>
          );
        })}
      </div>
    </ModalShell>
  );
}

const mapStateToProps = (state) => ({
  userInfo: userInfoSelector(state),
  companies: companiesSelector(state),
  people: peopleSelector(state),
  locations: locationsSelector(state),
  currentPersonId: currentPersonIdSelector(state),
  savedContractors: savedContractorsSelector(state),
  contractorCategories: contractorCategoriesSelector(state),
});

const actionCreators = {
  listInvestorTracker,
  saveLocation: saveLocationAction,
  deleteLocation: deleteLocationAction,
  saveExpense: saveExpenseAction,
  deleteExpense: deleteExpenseAction,
  savePayment: savePaymentAction,
  deletePayment: deletePaymentAction,
  updatePaymentStatus: updatePaymentStatusAction,
  updateParticipation: updateParticipationAction,
  saveContractor: saveContractorAction,
  deleteContractor: deleteContractorAction,
  saveQuote: saveQuoteAction,
  deleteQuote: deleteQuoteAction,
  saveContractorPayment: saveContractorPaymentAction,
  deleteContractorPayment: deleteContractorPaymentAction,
  saveWeeklyPayment: saveWeeklyPaymentAction,
  deleteWeeklyPayment: deleteWeeklyPaymentAction,
  saveSavedContractor: saveSavedContractorAction,
  deleteSavedContractor: deleteSavedContractorAction,
  saveContractorCategory: saveContractorCategoryAction,
};

export default withShipment({ mapStateToProps, actionCreators }, InvestmentTracker);

function ExpenseModal({ modal, onClose, people, locations, addExpense, updateExpense, addContractorPayment, activeCompanyId, expenseDescriptions, isAdmin, currentUserId }) {
  const loc = locations.find(l => l.id === modal.locationId);
  const isEdit = modal.kind === "editExpense";
  const existing = isEdit ? loc?.expenses.find(e => e.id === modal.expenseId) : null;
  const eligible = loc?.type === "financed" ? people : people.filter(p => loc?.participations?.some(pp => pp.personId === p.id));
  const contractors = loc?.contractors || [];
  const initialContractorId = existing?.linkedContractorId || "";

  // If we're editing a legacy expense whose description doesn't match
  // any current dropdown option, surface it as a transient option at
  // the top so the user can keep it or pick a new one. This avoids
  // silently rewriting historical data.
  const legacyDescription = (() => {
    if (!isEdit) return null;
    const d = existing?.description || "";
    if (!d) return null;
    if (expenseDescriptions.includes(d)) return null;
    if (initialContractorId) return null;
    return d;
  })();
  const dropdownOptions = legacyDescription ? [legacyDescription, ...expenseDescriptions] : expenseDescriptions;

  const initialDescription = (() => {
    if (isEdit) {
      if (initialContractorId && existing?.description && isContractorDescription(existing.description)) return existing.description;
      if (initialContractorId) return GENERAL_CONTRACTOR_DESCRIPTION;
      if (existing?.description && expenseDescriptions.includes(existing.description)) return existing.description;
      if (legacyDescription) return legacyDescription;
    }
    return dropdownOptions[0] || "";
  })();

  const [description, setDescription] = useState(initialDescription);
  const isContractorAssignment = isContractorDescription(description);
  // When a contractor-routing description is picked, only show contractors
  // tagged with the matching trade so the user can't accidentally route an
  // electrical expense to the camera contractor.
  const matchingContractors = isContractorAssignment
    ? contractors.filter(c => c.trade === description)
    : contractors;
  const [contractorId, setContractorId] = useState(initialContractorId || matchingContractors[0]?.id || "");
  // If the description changes, reset the contractor pick to the first
  // matching contractor (or clear if none match).
  useEffect(() => {
    if (!isContractorAssignment) return;
    const stillMatches = matchingContractors.some(c => c.id === contractorId);
    if (!stillMatches) setContractorId(matchingContractors[0]?.id || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [description]);
  const [amount, setAmount] = useState(existing?.amount?.toString() || "");
  // Auto-paid-by for non-admins. Only admins can override.
  const defaultPaidBy = (existing?.paidByPersonId)
    || (isAdmin ? (eligible[0]?.id || people[0]?.id) : currentUserId)
    || eligible[0]?.id
    || people[0]?.id;
  const [paidByPersonId, setPaidByPersonId] = useState(defaultPaidBy);
  const [note, setNote] = useState(existing?.note || "");
  const [date, setDate] = useState(existing?.date || new Date().toISOString().split("T")[0]);

  const noMatchingContractors = matchingContractors.length === 0;
  const effectivePaidBy = isAdmin ? paidByPersonId : currentUserId;
  const valid = description && amount && Number(amount) > 0 && effectivePaidBy && (!isContractorAssignment || (contractorId && !noMatchingContractors));

  const handleSave = () => {
    const payload = { description, amount: Number(amount), paidByPersonId: effectivePaidBy, date, note };
    let linkId = null;
    if (isContractorAssignment && contractorId) {
      payload.linkedContractorId = contractorId;
      // Reuse the existing linkId on edit so the server can move the
      // mirrored contractor.payments row instead of leaving an orphan.
      linkId = existing?.linkId || `link_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      payload.linkId = linkId;
    } else if (isEdit) {
      // Explicitly null these so the server clears the link if the
      // expense category changed away from a contractor-routing one.
      payload.linkedContractorId = null;
      payload.linkId = null;
    }
    if (isEdit) {
      updateExpense(modal.locationId, modal.expenseId, payload);
      onClose();
      return;
    }
    // The expense save and the mirrored contractor.payments save both
    // round-trip the entire location state. Running them in parallel
    // races: chain so the contractor side applies on top of the expense.
    if (isContractorAssignment && contractorId) {
      addExpense(modal.locationId, payload, (err) => {
        if (!err) {
          addContractorPayment(modal.locationId, contractorId, {
            amount: Number(amount),
            paidByPersonId: effectivePaidBy,
            method: "Cash",
            note: note || description,
            date,
            linkId,
          });
        }
      });
    } else {
      addExpense(modal.locationId, payload);
    }
    onClose();
  };

  const paidByPerson = getPerson(people, effectivePaidBy);
  return (
    <ModalShell onClose={onClose}>
      <ModalHeader title={isEdit ? "Edit Expense" : "Add Expense"} onClose={onClose} />
      <Field label="Description">
        {dropdownOptions.length === 0 ? (
          <div style={{ fontSize: 12, color: "#92400E", background: "#FEF3C7", border: "1px solid #FCD34D", borderRadius: 8, padding: "8px 10px" }}>
            No expense descriptions configured. Ask an admin to set them up in Expense Descriptions settings.
          </div>
        ) : (
          <select value={description} onChange={e => setDescription(e.target.value)} style={inputStyle}>
            {dropdownOptions.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        )}
      </Field>
      {isContractorAssignment && (
        <Field label={`Assign To ${description}`}>
          {noMatchingContractors ? (
            <div style={{ fontSize: 12, color: "#92400E", background: "#FEF3C7", border: "1px solid #FCD34D", borderRadius: 8, padding: "8px 10px" }}>
              No {description.toLowerCase()}s on this location yet. Add one from the Contractors tab first.
            </div>
          ) : (
            <>
              <select value={contractorId} onChange={e => setContractorId(e.target.value)} style={inputStyle}>
                {matchingContractors.map(c => <option key={c.id} value={c.id}>{c.trade ? `${c.name} (${c.trade})` : c.name}</option>)}
              </select>
              <div style={{ fontSize: 11, color: "#64748B", marginTop: 6, fontStyle: "italic" }}>
                This expense will be deducted from the selected contractor's balance.
              </div>
            </>
          )}
        </Field>
      )}
      <Field label="Amount ($)"><input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" style={inputStyle} /></Field>
      {isAdmin ? (
        <Field label="Paid By">
          <select value={paidByPersonId} onChange={e => setPaidByPersonId(e.target.value)} style={inputStyle}>
            {eligible.map(p => <option key={p.id} value={p.id}>{personLabel(p, activeCompanyId)}</option>)}
          </select>
        </Field>
      ) : (
        <Field label="Paid By">
          <div style={{ ...inputStyle, background: "#F8FAFC", color: "#475569", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>{cleanName(paidByPerson?.name) || "You"}</span>
            <span style={{ fontSize: 10, fontWeight: 700, background: "#ECFDF5", color: "#065F46", padding: "2px 6px", borderRadius: 4 }}>AUTO</span>
          </div>
        </Field>
      )}
      <Field label="Note (optional)">
        <input value={note} onChange={e => setNote(e.target.value)} placeholder="Deposit, terms, or any extra context" style={inputStyle} />
      </Field>
      <Field label="Date"><DatePicker value={date} onChange={setDate} /></Field>
      <PrimaryBtn onClick={handleSave} disabled={!valid}>{isEdit ? "Save Changes" : "Add Expense"}</PrimaryBtn>
    </ModalShell>
  );
}

function PaymentModal({ modal, onClose, people, locations, addPayment, isOwner, currentUserId, activeCompanyId }) {
  const loc = locations.find(l => l.id === modal.locationId);
  const eligibleFrom = loc?.participations || [];
  const eligibleTo = loc?.participations || [];
  const [fromPersonId, setFromPersonId] = useState(modal.fromPersonId || (modal.onBehalf ? eligibleFrom[0]?.personId : currentUserId));
  const [toPersonId, setToPersonId] = useState(modal.toPersonId || eligibleTo.find(p => p.personId !== (modal.fromPersonId || currentUserId))?.personId || eligibleTo[0]?.personId);
  const [amount, setAmount] = useState(modal.suggestedAmount?.toString() || "");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [source, setSource] = useState("Weekly Profit");
  const [note, setNote] = useState("");
  const onBehalf = modal.onBehalf || (isOwner && fromPersonId !== currentUserId);
  const valid = fromPersonId && toPersonId && fromPersonId !== toPersonId && amount && Number(amount) > 0;
  const handleSave = () => {
    const payload = { fromPersonId, toPersonId, amount: Number(amount), date, source, note };
    if (onBehalf) payload.onBehalfOfPersonId = fromPersonId;
    addPayment(modal.locationId, payload);
    onClose();
  };
  return (
    <ModalShell onClose={onClose}>
      <ModalHeader title="Log Payment" onClose={onClose} />
      {onBehalf && (
        <div style={{ background: "#FEF3C7", border: "1px solid #FCD34D", borderRadius: 8, padding: "8px 10px", marginBottom: 12, fontSize: 11, color: "#92400E" }}>
          Logging on behalf of {getPerson(people, fromPersonId).name}. They'll be notified.
        </div>
      )}
      <Field label="From">
        <select value={fromPersonId} onChange={e => setFromPersonId(e.target.value)} style={inputStyle}>
          {eligibleFrom.map(p => { const person = getPerson(people, p.personId); return <option key={p.personId} value={p.personId}>{personLabel(person, activeCompanyId)}</option>; })}
        </select>
      </Field>
      <Field label="To">
        <select value={toPersonId} onChange={e => setToPersonId(e.target.value)} style={inputStyle}>
          {eligibleTo.map(p => { const person = getPerson(people, p.personId); return <option key={p.personId} value={p.personId}>{personLabel(person, activeCompanyId)}</option>; })}
        </select>
      </Field>
      <Field label="Amount ($)"><input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" style={inputStyle} /></Field>
      <Field label="Source">
        <select value={source} onChange={e => setSource(e.target.value)} style={inputStyle}>
          {["Weekly Profit", "Cash Payment", "Zelle", "Check", "Wire Transfer"].map(s => <option key={s}>{s}</option>)}
        </select>
      </Field>
      <Field label="Date"><DatePicker value={date} onChange={setDate} /></Field>
      <Field label="Note (optional)"><input value={note} onChange={e => setNote(e.target.value)} placeholder="Optional note" style={inputStyle} /></Field>
      <PrimaryBtn onClick={handleSave} disabled={!valid}>Log Payment</PrimaryBtn>
    </ModalShell>
  );
}

function LocationModal({ modal, onClose, locations, people, saveLocation, activeCompanyId }) {
  const existing = modal.kind === "editLocation" ? locations.find(l => l.id === modal.locationId) : null;
  const initialParticipantIds = existing?.participations?.map(p => p.personId) || [];
  const initialParticipations = (existing?.participations || []).reduce((acc, p) => {
    acc[p.personId] = {
      percentage: p.percentage != null ? String(p.percentage) : "",
      contributed: p.contributed != null ? String(p.contributed) : "",
    };
    return acc;
  }, {});
  // Reuse the financedRoster helper so legacy single-financier records
  // (no financedInvestorIds list) still pre-populate the toggle list.
  const initialFinancedInvestors = existing && existing.type === "financed" ? financedRoster(existing) : [];

  const [name, setName] = useState(existing?.name || "");
  const [address, setAddress] = useState(existing?.address || "");
  const [openDate, setOpenDate] = useState(existing?.openDate || "");
  const [totalInvestment, setTotalInvestment] = useState(existing?.totalInvestment != null ? String(existing.totalInvestment) : "");
  const [type, setType] = useState(existing?.type || "shared");
  const investorPool = people.filter(p => {
    const m = (p.memberships || []).find(x => x.companyId === activeCompanyId);
    return !!m;
  });
  const [financedInvestorIds, setFinancedInvestorIds] = useState(initialFinancedInvestors);
  const [participantIds, setParticipantIds] = useState(initialParticipantIds);
  const [participations, setParticipations] = useState(initialParticipations);
  const toggleFinancedInvestor = (id) => {
    setFinancedInvestorIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const togglePerson = (id) => {
    if (participantIds.includes(id)) {
      setParticipantIds(participantIds.filter(x => x !== id));
      const np = { ...participations }; delete np[id]; setParticipations(np);
    } else {
      setParticipantIds([...participantIds, id]);
      setParticipations({ ...participations, [id]: { percentage: "", contributed: "" } });
    }
  };
  const totalPct = participantIds.reduce((s, id) => s + (Number(participations[id]?.percentage) || 0), 0);
  const pctValid = participantIds.length === 0 || participantIds.every(id => !participations[id]?.percentage) || totalPct === 100;
  const totalInvestmentRequired = type === "financed";
  const valid = name && address && (totalInvestmentRequired ? (totalInvestment && Number(totalInvestment) > 0 && financedInvestorIds.length > 0) : participantIds.length > 0) && pctValid;
  const handleSave = () => {
    const base = { name, address, openDate: openDate || "TBD", type };
    if (existing) base.id = existing.id;
    if (activeCompanyId) base.companyId = activeCompanyId;
    if (type === "financed") {
      base.totalInvestment = Number(totalInvestment);
      // First selected investor doubles as the legacy single financier
      // so existing screens that read financedByPersonId stay valid.
      base.financedByPersonId = financedInvestorIds[0] || null;
      base.financedInvestorIds = financedInvestorIds;
      base.participations = [];
    } else {
      base.participations = participantIds.map(id => ({
        personId: id,
        percentage: participations[id]?.percentage ? Number(participations[id].percentage) : null,
        contributed: participations[id]?.contributed ? Number(participations[id].contributed) : 0,
      }));
      base.totalInvestment = base.participations.reduce((s, p) => s + (p.contributed || 0), 0);
    }
    saveLocation(base);
    onClose();
  };
  return (
    <ModalShell onClose={onClose}>
      <ModalHeader title={existing ? "Edit Location" : "New Location"} onClose={onClose} />
      <Field label="Name"><input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Royal Crown" style={inputStyle} /></Field>
      <Field label="Address"><input value={address} onChange={e => setAddress(e.target.value)} placeholder="e.g. Bellfort, Houston TX" style={inputStyle} /></Field>
      <Field label="Open Date"><input value={openDate} onChange={e => setOpenDate(e.target.value)} placeholder="e.g. May 2025" style={inputStyle} /></Field>
      <Field label="Type">
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setType("shared")} style={{ flex: 1, padding: "10px", border: type === "shared" ? "1.5px solid #4F46E5" : "1px solid #E2E8F0", background: type === "shared" ? "#EEF2FF" : "white", color: type === "shared" ? "#4338CA" : "#64748B", borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>Shared</button>
          <button onClick={() => setType("financed")} style={{ flex: 1, padding: "10px", border: type === "financed" ? "1.5px solid #4F46E5" : "1px solid #E2E8F0", background: type === "financed" ? "#EEF2FF" : "white", color: type === "financed" ? "#4338CA" : "#64748B", borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>Financed</button>
        </div>
      </Field>
      {type === "financed" ? (
        <>
          <Field label="Total Investment ($)"><input type="number" value={totalInvestment} onChange={e => setTotalInvestment(e.target.value)} placeholder="0" style={inputStyle} /></Field>
          <Field label="Investors (tap to toggle)">
            <div style={{ fontSize: 11, color: "#64748B", marginBottom: 8 }}>
              Selected investors can log weekly payments on this location.
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {investorPool.map(p => {
                const isOn = financedInvestorIds.includes(p.id);
                const color = colorForPerson(p.id, people);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => toggleFinancedInvestor(p.id)}
                    style={{
                      padding: "6px 10px",
                      border: isOn ? `1.5px solid ${color.accent}` : "1px solid #E2E8F0",
                      background: isOn ? color.bg : "white",
                      color: isOn ? color.text : "#64748B",
                      borderRadius: 8,
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: "pointer",
                      fontFamily: FONT,
                    }}
                  >
                    {personLabel(p, activeCompanyId)}
                  </button>
                );
              })}
            </div>
            {financedInvestorIds.length === 0 && (
              <div style={{ fontSize: 11, marginTop: 6, color: "#92400E", fontWeight: 600 }}>
                Select at least one investor.
              </div>
            )}
          </Field>
        </>
      ) : (
        <Field label="Investors (tap to toggle)">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
            {investorPool.map(p => {
              const isOn = participantIds.includes(p.id);
              const color = colorForPerson(p.id, people);
              return <button key={p.id} onClick={() => togglePerson(p.id)} style={{ padding: "6px 10px", border: isOn ? `1.5px solid ${color.accent}` : "1px solid #E2E8F0", background: isOn ? color.bg : "white", color: isOn ? color.text : "#64748B", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>{personLabel(p, activeCompanyId)}</button>;
            })}
          </div>
          {participantIds.map(id => {
            const person = getPerson(people, id);
            return (
              <div key={id} style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: 10, marginBottom: 6 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#1E293B", marginBottom: 4 }}>{personLabel(person, activeCompanyId)}</div>
                <div style={{ display: "flex", gap: 6 }}>
                  <input type="number" placeholder="%" value={participations[id]?.percentage || ""} onChange={e => setParticipations({ ...participations, [id]: { ...participations[id], percentage: e.target.value } })} style={{ ...inputStyle, fontSize: 12, padding: "8px 10px" }} />
                  <input type="number" placeholder="Contributed $" value={participations[id]?.contributed || ""} onChange={e => setParticipations({ ...participations, [id]: { ...participations[id], contributed: e.target.value } })} style={{ ...inputStyle, fontSize: 12, padding: "8px 10px" }} />
                </div>
              </div>
            );
          })}
          {participantIds.length > 0 && participantIds.some(id => participations[id]?.percentage) && (
            <div style={{ fontSize: 11, marginTop: 6, color: totalPct === 100 ? "#065F46" : "#92400E", fontWeight: 600 }}>
              Total: {totalPct}% {totalPct === 100 ? "✓" : `(must equal 100% or leave all blank)`}
            </div>
          )}
        </Field>
      )}
      <PrimaryBtn onClick={handleSave} disabled={!valid}>{existing ? "Save Changes" : "Create Location"}</PrimaryBtn>
    </ModalShell>
  );
}

function ParticipationModal({ modal, onClose, people, locations, updateParticipation }) {
  const loc = locations.find(l => l.id === modal.locationId);
  const part = loc?.participations.find(p => p.personId === modal.personId);
  const person = getPerson(people, modal.personId);
  const [percentage, setPercentage] = useState(part?.percentage?.toString() || "");
  const [contributed, setContributed] = useState(part?.contributed?.toString() || "");
  const valid = percentage === "" || (Number(percentage) >= 0 && Number(percentage) <= 100);
  const handleSave = () => {
    updateParticipation(modal.locationId, modal.personId, { percentage: percentage === "" ? null : Number(percentage), contributed: Number(contributed || 0) });
    onClose();
  };
  return (
    <ModalShell onClose={onClose}>
      <ModalHeader title={`Edit ${cleanName(person.name)}'s participation`} onClose={onClose} />
      <Field label="Ownership Percentage (%)"><input type="number" min="0" max="100" value={percentage} onChange={e => setPercentage(e.target.value)} placeholder="0-100" style={inputStyle} /></Field>
      <Field label="Total Contributed ($)"><input type="number" value={contributed} onChange={e => setContributed(e.target.value)} placeholder="0" style={inputStyle} /></Field>
      <PrimaryBtn onClick={handleSave} disabled={!valid}>Save Changes</PrimaryBtn>
    </ModalShell>
  );
}

// Category picker for contractors. Renders the saved category list plus
// an inline "+ Add new category…" affordance so users aren't limited to a
// hardcoded set — typing a new one (e.g. "Painter") persists it via
// onAddCategory so it appears in every category dropdown from then on.
function CategorySelect({ value, onChange, categories = [], onAddCategory }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  // Make sure the current value is always selectable even if it isn't in
  // the saved list yet (e.g. a legacy contractor with an old trade).
  const options = categories.includes(value) || !value ? categories : [...categories, value];
  const commitNew = () => {
    const next = draft.trim();
    if (!next) return;
    if (typeof onAddCategory === "function") onAddCategory(next);
    onChange(next);
    setDraft("");
    setAdding(false);
  };
  if (adding) {
    return (
      <div style={{ display: "flex", gap: 8 }}>
        <input
          autoFocus
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); commitNew(); } }}
          placeholder="e.g. Painter"
          style={inputStyle}
        />
        <button
          type="button"
          onClick={commitNew}
          disabled={!draft.trim()}
          style={{ padding: "0 16px", border: "none", borderRadius: 10, background: draft.trim() ? C.primaryGreen : "#CBD5E1", color: "white", fontFamily: FONT, fontSize: 13, fontWeight: 700, cursor: draft.trim() ? "pointer" : "not-allowed" }}
        >
          Add
        </button>
        <button
          type="button"
          onClick={() => { setAdding(false); setDraft(""); }}
          style={{ padding: "0 12px", border: "1px solid #E2E8F0", borderRadius: 10, background: "white", color: "#64748B", fontFamily: FONT, fontSize: 13, fontWeight: 700, cursor: "pointer" }}
        >
          Cancel
        </button>
      </div>
    );
  }
  return (
    <select
      value={value}
      onChange={e => {
        if (e.target.value === "__add_new__") { setAdding(true); return; }
        onChange(e.target.value);
      }}
      style={inputStyle}
    >
      <option value="">Select a category</option>
      {options.map(t => <option key={t} value={t}>{t}</option>)}
      <option value="__add_new__">+ Add new category…</option>
    </select>
  );
}

function ContractorModal({ modal, onClose, addContractor, locations, savedContractors = [], contractorCategories = [], addContractorCategory }) {
  const isEdit = modal.kind === "editContractor";
  const loc = locations.find(l => l.id === modal.locationId);
  const locContractors = loc?.contractors || [];
  const existing = isEdit
    ? locContractors.find(c => c.id === modal.contractorId)
    : null;
  const [name, setName] = useState(existing?.name || "");
  const [trade, setTrade] = useState(existing?.trade || "");
  const [phone, setPhone] = useState(existing?.phone || "");
  // Quick-pick: featured saved contractors (autoShow=true) up top, the
  // archived ones revealed on demand so the modal doesn't bury the
  // common cases.
  const [showArchived, setShowArchived] = useState(false);
  const featured = savedContractors.filter(s => s.autoShow !== false);
  const archived = savedContractors.filter(s => s.autoShow === false);
  const contractorKey = (n, t) => `${(n || "").toLowerCase().trim()}|${(t || "").toLowerCase().trim()}`;
  // Existing contractors on this location keyed for duplicate detection.
  // Edit mode excludes the row being edited so the user can resave it.
  const takenKeys = new Set(
    locContractors
      .filter(c => !isEdit || c.id !== existing?.id)
      .map(c => contractorKey(c.name, c.trade))
  );
  const isDuplicate = !!name && !!trade && takenKeys.has(contractorKey(name, trade));
  const fillFromSaved = (sc) => {
    setName(sc.name || "");
    setTrade(sc.trade || "");
    setPhone(sc.phone || "");
  };
  const valid = name && trade && !isDuplicate;
  const handleSave = () => {
    if (!valid) return;
    if (isEdit) {
      addContractor(modal.locationId, { id: existing.id, name, trade, phone });
    } else {
      addContractor(modal.locationId, { name, trade, phone, quotes: [], payments: [] });
    }
    onClose();
  };
  return (
    <ModalShell onClose={onClose}>
      <ModalHeader title={isEdit ? "Edit Contractor" : "New Contractor"} onClose={onClose} />
      {!isEdit && (featured.length > 0 || archived.length > 0) && (
        <Field label="Pick from saved">
          {featured.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {featured.map(sc => {
                const taken = takenKeys.has(contractorKey(sc.name, sc.trade));
                return (
                  <button
                    key={sc.id}
                    type="button"
                    onClick={() => { if (!taken) fillFromSaved(sc); }}
                    disabled={taken}
                    title={taken ? "Already on this location" : (sc.phone || "")}
                    style={{ padding: "6px 10px", border: `1px solid ${taken ? "#E2E8F0" : "#C7D2FE"}`, background: taken ? "#F1F5F9" : "#EEF2FF", color: taken ? "#94A3B8" : "#4338CA", borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: taken ? "not-allowed" : "pointer", fontFamily: FONT, textDecoration: taken ? "line-through" : "none" }}
                  >
                    {sc.name}{sc.trade ? ` · ${sc.trade}` : ""}{taken ? " · added" : ""}
                  </button>
                );
              })}
            </div>
          ) : (
            <div style={{ fontSize: 11, color: "#94A3B8" }}>No featured saved contractors. Toggle Auto-show on entries in Settings to feature them.</div>
          )}
          {archived.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <button
                type="button"
                onClick={() => setShowArchived(s => !s)}
                style={{ background: "transparent", border: "none", color: "#64748B", fontFamily: FONT, fontSize: 11, fontWeight: 700, cursor: "pointer", padding: 0 }}
              >
                {showArchived ? "Hide" : "Show"} archived ({archived.length})
              </button>
              {showArchived && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                  {archived.map(sc => {
                    const taken = takenKeys.has(contractorKey(sc.name, sc.trade));
                    return (
                      <button
                        key={sc.id}
                        type="button"
                        onClick={() => { if (!taken) fillFromSaved(sc); }}
                        disabled={taken}
                        title={taken ? "Already on this location" : ""}
                        style={{ padding: "6px 10px", border: "1px solid #E2E8F0", background: taken ? "#F1F5F9" : "white", color: taken ? "#94A3B8" : "#64748B", borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: taken ? "not-allowed" : "pointer", fontFamily: FONT, textDecoration: taken ? "line-through" : "none" }}
                      >
                        {sc.name}{sc.trade ? ` · ${sc.trade}` : ""}{taken ? " · added" : ""}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </Field>
      )}
      <Field label="Name"><input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Lalo Mitchell" style={inputStyle} /></Field>
      <Field label="Category">
        <CategorySelect value={trade} onChange={setTrade} categories={contractorCategories} onAddCategory={addContractorCategory} />
      </Field>
      {isDuplicate && (
        <div style={{ fontSize: 11, color: "#92400E", background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 8, padding: "8px 10px", marginTop: -8, marginBottom: 14, fontWeight: 600 }}>
          A contractor named <b>{name}</b>{trade ? ` (${trade})` : ""} is already on this location. Edit the existing one instead of adding a duplicate.
        </div>
      )}
      <Field label="Phone"><input value={phone} onChange={e => setPhone(e.target.value)} placeholder="(832) 555-0100" style={inputStyle} /></Field>
      <PrimaryBtn onClick={handleSave} disabled={!valid}>{isEdit ? "Save Changes" : isDuplicate ? "Already Added" : "Add Contractor"}</PrimaryBtn>
    </ModalShell>
  );
}

function ConfirmDeleteModal({ title, message, onClose, onConfirm, confirmLabel = "Delete" }) {
  return (
    <ModalShell onClose={onClose}>
      <ModalHeader title={title} onClose={onClose} />
      <div style={{ fontSize: 13, color: "#1E293B", lineHeight: 1.5, marginBottom: 16 }}>{message}</div>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={onClose} style={{ flex: 1, padding: "12px", background: "#F1F5F9", color: "#475569", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>Cancel</button>
        <button
          onClick={() => { onConfirm(); onClose(); }}
          style={{ flex: 1, padding: "12px", background: "#DC2626", color: "white", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}
        >
          {confirmLabel}
        </button>
      </div>
    </ModalShell>
  );
}

function DeleteContractorModal({ modal, onClose, locations, removeContractor, onAfterDelete }) {
  const loc = locations.find(l => l.id === modal.locationId);
  const contractor = (loc?.contractors || []).find(c => c.id === modal.contractorId);
  if (!contractor) { onClose(); return null; }
  const linkedExpenses = (loc.expenses || []).filter(e => e.linkedContractorId === contractor.id);
  const linkedTotal = linkedExpenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);
  return (
    <ModalShell onClose={onClose}>
      <ModalHeader title="Delete Contractor" onClose={onClose} />
      <div style={{ fontSize: 13, color: "#1E293B", lineHeight: 1.5, marginBottom: 12 }}>
        Remove <b>{contractor.name}</b>{contractor.trade ? ` (${contractor.trade})` : ""} from this location?
      </div>
      {linkedExpenses.length > 0 && (
        <div style={{ background: "#FEF3C7", border: "1px solid #FCD34D", borderRadius: 10, padding: "10px 12px", fontSize: 12, color: "#92400E", marginBottom: 16 }}>
          {linkedExpenses.length} linked expense{linkedExpenses.length === 1 ? "" : "s"} totaling {fmt(linkedTotal)} will also be removed. The investor's Paid total will drop by this amount.
        </div>
      )}
      {linkedExpenses.length === 0 && (
        <div style={{ fontSize: 12, color: "#64748B", marginBottom: 16 }}>
          No linked expenses found.
        </div>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={onClose} style={{ flex: 1, padding: "12px", background: "#F1F5F9", color: "#475569", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>Cancel</button>
        <button
          onClick={() => {
            removeContractor(modal.locationId, contractor.id);
            if (typeof onAfterDelete === "function") onAfterDelete();
            onClose();
          }}
          style={{ flex: 1, padding: "12px", background: "#DC2626", color: "white", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}
        >
          Delete
        </button>
      </div>
    </ModalShell>
  );
}

function QuoteModal({ modal, onClose, locations, addQuote }) {
  const isEdit = modal.kind === "editQuote";
  const existing = isEdit
    ? (locations.find(l => l.id === modal.locationId)?.contractors || [])
        .find(c => c.id === modal.contractorId)?.quotes
        ?.find(q => q.id === modal.quoteId)
    : null;
  const [description, setDescription] = useState(existing?.description || "");
  const [amount, setAmount] = useState(existing?.amount != null ? String(existing.amount) : "");
  const [type, setType] = useState(existing?.type || (modal.hasInitial ? "extra" : "initial"));
  const [date, setDate] = useState(existing?.date || new Date().toISOString().split("T")[0]);
  const valid = description && amount && Number(amount) > 0;
  const initialDisabled = isEdit ? existing?.type !== "initial" : modal.hasInitial;
  return (
    <ModalShell onClose={onClose}>
      <ModalHeader title={isEdit ? "Edit Quote" : "Add Quote"} onClose={onClose} />
      <Field label="Description"><input value={description} onChange={e => setDescription(e.target.value)} placeholder="What's this quote for?" style={inputStyle} /></Field>
      <Field label="Amount ($)"><input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" style={inputStyle} /></Field>
      <Field label="Type">
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => !initialDisabled && !isEdit && setType("initial")} disabled={initialDisabled || isEdit} style={{ flex: 1, padding: "10px", border: type === "initial" ? "1.5px solid #F97316" : "1px solid #E2E8F0", background: type === "initial" ? "#FFF7ED" : initialDisabled ? "#F8FAFC" : "white", color: type === "initial" ? "#9A3412" : initialDisabled ? "#CBD5E1" : "#64748B", borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: (initialDisabled || isEdit) ? "not-allowed" : "pointer", fontFamily: FONT }}>Initial{modal.hasInitial && !isEdit ? " (set)" : ""}</button>
          <button onClick={() => !isEdit && setType("extra")} disabled={isEdit} style={{ flex: 1, padding: "10px", borderRadius: 10, border: type === "extra" ? "1.5px solid #F97316" : "1px solid #E2E8F0", background: type === "extra" ? "#FFF7ED" : "white", color: type === "extra" ? "#9A3412" : "#64748B", fontSize: 12, fontWeight: 700, cursor: isEdit ? "not-allowed" : "pointer", fontFamily: FONT }}>Extra</button>
        </div>
      </Field>
      <Field label="Date"><DatePicker value={date} onChange={setDate} /></Field>
      <PrimaryBtn onClick={() => {
        const quote = { description, amount: Number(amount), type, date };
        if (isEdit) quote.id = existing.id;
        addQuote(modal.locationId, modal.contractorId, quote);
        onClose();
      }} disabled={!valid} color="#F97316">{isEdit ? "Save Changes" : "Add Quote"}</PrimaryBtn>
    </ModalShell>
  );
}

function ContractorPaymentModal({ modal, onClose, locations, people, addContractorPayment, activeCompanyId }) {
  const isEdit = modal.kind === "editContractorPayment";
  const existing = isEdit
    ? (locations.find(l => l.id === modal.locationId)?.contractors || [])
        .find(c => c.id === modal.contractorId)?.payments
        ?.find(p => p.id === modal.paymentId)
    : null;
  const investors = people.filter(p => (p.memberships || []).length > 0);
  const [amount, setAmount] = useState(existing?.amount != null ? String(existing.amount) : "");
  const [paidByPersonId, setPaidByPersonId] = useState(existing?.paidByPersonId || investors[0]?.id || people[0]?.id);
  const [method, setMethod] = useState(existing?.method || "Cash");
  const [note, setNote] = useState(existing?.note || "");
  const [date, setDate] = useState(existing?.date || new Date().toISOString().split("T")[0]);
  const valid = amount && Number(amount) > 0 && paidByPersonId;
  return (
    <ModalShell onClose={onClose}>
      <ModalHeader title={isEdit ? "Edit Payment" : "Pay Contractor"} onClose={onClose} />
      <Field label="Amount ($)"><input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" style={inputStyle} /></Field>
      <Field label="Paid By">
        <select value={paidByPersonId} onChange={e => setPaidByPersonId(e.target.value)} style={inputStyle}>
          {investors.map(p => <option key={p.id} value={p.id}>{personLabel(p, activeCompanyId)}</option>)}
        </select>
      </Field>
      <Field label="Method">
        <select value={method} onChange={e => setMethod(e.target.value)} style={inputStyle}>
          {["Cash", "Zelle", "Check", "Wire Transfer", "Other"].map(m => <option key={m}>{m}</option>)}
        </select>
      </Field>
      <Field label="Note"><input value={note} onChange={e => setNote(e.target.value)} placeholder="Optional" style={inputStyle} /></Field>
      <Field label="Date"><DatePicker value={date} onChange={setDate} /></Field>
      <PrimaryBtn onClick={() => {
        const payment = { amount: Number(amount), paidByPersonId, method, note, date };
        if (isEdit) payment.id = existing.id;
        addContractorPayment(modal.locationId, modal.contractorId, payment);
        onClose();
      }} disabled={!valid} color="#10B981">{isEdit ? "Save Changes" : "Log Payment"}</PrimaryBtn>
    </ModalShell>
  );
}

// Monday-anchored week range starting on the day passed in. Returns
// the inclusive [start, end] dates plus a "Sep 22 - Sep 28" label so
// the WeekPicker output mirrors the format the spec calls out.
const computeWeekRange = (iso) => {
  const d = parseISO(iso);
  if (!d) return null;
  // JS getDay(): Sun=0..Sat=6. Shift so Mon=0..Sun=6 for a Mon–Sun week.
  const dow = (d.getDay() + 6) % 7;
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate() - dow);
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
  return { start, end };
};

const formatWeekLabel = (iso) => {
  const r = computeWeekRange(iso);
  if (!r) return "";
  const f = (dt) => dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `${f(r.start)} - ${f(r.end)}`;
};

// Calendar-style week picker. Behaves like the existing DatePicker but
// the trigger displays the week label and clicking a day selects the
// whole week the day belongs to. The hosting form decides what to do
// with the label (the WeeklyPaymentModal stores it as `week`).
function WeekPicker({ value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("days");
  const [cursor, setCursor] = useState(() => {
    const init = parseISO(value) || new Date();
    return new Date(init.getFullYear(), init.getMonth(), 1);
  });
  const rootRef = React.useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const openPicker = () => {
    const init = parseISO(value) || new Date();
    setCursor(new Date(init.getFullYear(), init.getMonth(), 1));
    setView("days");
    setOpen(true);
  };
  const prevMonth = () => setCursor(c => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  const nextMonth = () => setCursor(c => new Date(c.getFullYear(), c.getMonth() + 1, 1));
  const prevYear = () => setCursor(c => new Date(c.getFullYear() - 1, c.getMonth(), 1));
  const nextYear = () => setCursor(c => new Date(c.getFullYear() + 1, c.getMonth(), 1));
  const prevYearPage = () => setCursor(c => new Date(c.getFullYear() - 12, c.getMonth(), 1));
  const nextYearPage = () => setCursor(c => new Date(c.getFullYear() + 12, c.getMonth(), 1));
  const selectMonth = (monthIdx) => {
    setCursor(c => new Date(c.getFullYear(), monthIdx, 1));
    setView("days");
  };
  const selectYear = (yr) => {
    setCursor(c => new Date(yr, c.getMonth(), 1));
    setView("months");
  };
  const selectDay = (day) => {
    const d = new Date(cursor.getFullYear(), cursor.getMonth(), day);
    onChange(toISO(d));
    setOpen(false);
  };
  const selectThisWeek = () => {
    const today = new Date();
    onChange(toISO(today));
    setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
    setView("days");
    setOpen(false);
  };
  const cycleView = () => {
    if (view === "days") setView("months");
    else if (view === "months") setView("years");
    else setView("days");
  };

  const range = value ? computeWeekRange(value) : null;
  const today = new Date();
  // Monday-first calendar: shift Sun=0..Sat=6 to Mon=0..Sun=6 so the
  // leading blanks line up under the weekday header below.
  const firstWeekday = (new Date(cursor.getFullYear(), cursor.getMonth(), 1).getDay() + 6) % 7;
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  const displayValue = formatWeekLabel(value);
  const WP_WEEKDAYS = ["M", "T", "W", "T", "F", "S", "S"];

  const inWeek = (cellDate) => {
    if (!range) return false;
    return cellDate >= range.start && cellDate <= range.end;
  };

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <button type="button" onClick={() => { if (open) setOpen(false); else openPicker(); }}
        style={{ width: "100%", padding: "12px 14px", fontSize: 15, fontFamily: FONT, textAlign: "left", border: `2px solid ${open ? C.primaryGreen : C.border}`, borderRadius: 12, background: C.white, color: displayValue ? C.navyDark : C.muted, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", boxSizing: "border-box" }}>
        <span>{displayValue || placeholder || "Select a week"}</span>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4 M8 2v4 M3 10h18" />
        </svg>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 8px)", left: 0, right: 0, background: C.white, borderRadius: 16, boxShadow: "0 16px 40px rgba(15, 23, 42, 0.22)", border: `1px solid ${C.border}`, padding: 16, zIndex: 9100, fontFamily: FONT }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <button type="button" onClick={view === "days" ? prevMonth : view === "months" ? prevYear : prevYearPage} style={{ background: "#F1F5F9", border: "none", width: 32, height: 32, borderRadius: 8, cursor: "pointer", color: C.navyDark, display: "flex", alignItems: "center", justifyContent: "center" }} aria-label="Previous">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
            </button>
            <button type="button" onClick={cycleView}
              style={{ background: "transparent", border: "none", fontSize: 15, fontWeight: 700, color: C.navyDark, fontFamily: FONT, cursor: "pointer", padding: "4px 10px", borderRadius: 8, display: "flex", alignItems: "center", gap: 4 }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#F1F5F9"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              aria-label="Switch view">
              {view === "days" && `${DP_MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`}
              {view === "months" && cursor.getFullYear()}
              {view === "years" && (() => { const [s, e] = yearPage(cursor.getFullYear()); return `${s} – ${e}`; })()}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d={view === "years" ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"} />
              </svg>
            </button>
            <button type="button" onClick={view === "days" ? nextMonth : view === "months" ? nextYear : nextYearPage} style={{ background: "#F1F5F9", border: "none", width: 32, height: 32, borderRadius: 8, cursor: "pointer", color: C.navyDark, display: "flex", alignItems: "center", justifyContent: "center" }} aria-label="Next">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
            </button>
          </div>
          {view === "days" ? (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 6 }}>
                {WP_WEEKDAYS.map((w, i) => (
                  <div key={`${w}-${i}`} style={{ textAlign: "center", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, padding: "4px 0" }}>{w}</div>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
                {cells.map((day, idx) => {
                  if (day === null) return <div key={`b-${idx}`} />;
                  const cellDate = new Date(cursor.getFullYear(), cursor.getMonth(), day);
                  const isInWeek = inWeek(cellDate);
                  const isToday = sameDay(cellDate, today);
                  let bg = "transparent"; let color = C.navyDark; let fontWeight = 500; let border = "1px solid transparent";
                  if (isInWeek) { bg = C.primaryGreen; color = C.white; fontWeight = 700; }
                  else if (isToday) { border = `1px solid ${C.primaryGreen}`; color = C.primaryGreen; fontWeight = 700; }
                  return (
                    <button key={day} type="button" onClick={() => selectDay(day)}
                      style={{ background: bg, color, fontWeight, border, borderRadius: 10, height: 36, cursor: "pointer", fontFamily: FONT, fontSize: 14 }}
                      onMouseEnter={(e) => { if (!isInWeek) e.currentTarget.style.background = "#F0FDF4"; }}
                      onMouseLeave={(e) => { if (!isInWeek) e.currentTarget.style.background = "transparent"; }}>
                      {day}
                    </button>
                  );
                })}
              </div>
            </>
          ) : view === "months" ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
              {DP_MONTHS_SHORT.map((m, idx) => {
                const isCurrent = idx === cursor.getMonth();
                const isThisMonth = today.getFullYear() === cursor.getFullYear() && today.getMonth() === idx;
                let bg = "transparent"; let color = C.navyDark; let fontWeight = 500; let border = "1px solid transparent";
                if (isCurrent) { bg = C.primaryGreen; color = C.white; fontWeight = 700; }
                else if (isThisMonth) { border = `1px solid ${C.primaryGreen}`; color = C.primaryGreen; fontWeight = 700; }
                return (
                  <button key={m} type="button" onClick={() => selectMonth(idx)}
                    style={{ background: bg, color, fontWeight, border, borderRadius: 10, height: 48, cursor: "pointer", fontFamily: FONT, fontSize: 14, transition: "background 0.12s, color 0.12s" }}
                    onMouseEnter={(e) => { if (!isCurrent) e.currentTarget.style.background = "#F0FDF4"; }}
                    onMouseLeave={(e) => { if (!isCurrent) e.currentTarget.style.background = "transparent"; }}>
                    {m}
                  </button>
                );
              })}
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
              {(() => {
                const [startYr] = yearPage(cursor.getFullYear());
                const years = [];
                for (let i = 0; i < 12; i += 1) years.push(startYr + i);
                return years.map((yr) => {
                  const isCurrent = yr === cursor.getFullYear();
                  const isThisYear = yr === today.getFullYear();
                  let bg = "transparent"; let color = C.navyDark; let fontWeight = 500; let border = "1px solid transparent";
                  if (isCurrent) { bg = C.primaryGreen; color = C.white; fontWeight = 700; }
                  else if (isThisYear) { border = `1px solid ${C.primaryGreen}`; color = C.primaryGreen; fontWeight = 700; }
                  return (
                    <button key={yr} type="button" onClick={() => selectYear(yr)}
                      style={{ background: bg, color, fontWeight, border, borderRadius: 10, height: 48, cursor: "pointer", fontFamily: FONT, fontSize: 14, transition: "background 0.12s, color 0.12s" }}
                      onMouseEnter={(e) => { if (!isCurrent) e.currentTarget.style.background = "#F0FDF4"; }}
                      onMouseLeave={(e) => { if (!isCurrent) e.currentTarget.style.background = "transparent"; }}>
                      {yr}
                    </button>
                  );
                });
              })()}
            </div>
          )}
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <button type="button" onClick={selectThisWeek} style={{ background: "transparent", border: "none", color: C.primaryGreen, fontFamily: FONT, fontSize: 13, fontWeight: 700, cursor: "pointer", padding: "6px 8px" }}>This Week</button>
            <button type="button" onClick={() => setOpen(false)} style={{ background: "transparent", border: "none", color: C.muted, fontFamily: FONT, fontSize: 13, fontWeight: 600, cursor: "pointer", padding: "6px 8px" }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

function WeeklyPaymentModal({ modal, onClose, locations, people, addWeeklyPayment, updateWeeklyPayment, activeCompanyId, currentUserId }) {
  const isEdit = modal.kind === "editWeeklyPayment";
  const loc = locations.find(l => l.id === modal.locationId);
  const existing = isEdit ? (loc?.weeklyPayments || []).find(w => w.id === modal.weeklyPaymentId) : null;

  // Pool of investors who can be credited as the payer. Falls back to
  // every company person if the location has no roster yet — keeps
  // legacy single-financier locations usable.
  const roster = loc ? financedRoster(loc) : [];
  const investorPool = (roster.length > 0 ? roster : people.map(p => p.id))
    .map(id => getPerson(people, id))
    .filter(p => p.id);

  const today = new Date().toISOString().split("T")[0];

  // Decide initial week-picker mode: if the existing label matches the
  // auto-format from any reasonable anchor, default to calendar mode;
  // otherwise default to custom so the user's free-form label survives.
  const initialMode = (() => {
    if (!existing) return "calendar";
    if (existing.date && formatWeekLabel(existing.date) === existing.week) return "calendar";
    return "custom";
  })();

  const [weekMode, setWeekMode] = useState(initialMode);
  const [weekAnchor, setWeekAnchor] = useState(existing?.date || today);
  const [weekCustom, setWeekCustom] = useState(initialMode === "custom" ? (existing?.week || "") : "");
  const [amount, setAmount] = useState(existing?.amount != null ? String(existing.amount) : "");
  const [date, setDate] = useState(existing?.date || today);
  const [status, setStatus] = useState(existing?.status || "pending");
  const [note, setNote] = useState(existing?.note || "");
  // Default to the logged-in investor when they're on this location's
  // roster — every weekly payment has a creator/payer, so we never want
  // a card to render with "No payer assigned" if it can be avoided.
  // Editing a saved record honors whatever was previously set.
  const defaultPayer = existing
    ? (existing.paidByPersonId || "")
    : (investorPool.some(p => p.id === currentUserId) ? currentUserId : "");
  const [paidByPersonId, setPaidByPersonId] = useState(defaultPayer);
  const [paidDate, setPaidDate] = useState(existing?.paidDate || (existing?.status === "paid" ? existing?.date || today : today));

  const computedWeek = weekMode === "calendar" ? formatWeekLabel(weekAnchor) : weekCustom;
  const valid = computedWeek && amount !== "" && Number(amount) >= 0 && paidByPersonId;

  const handleSave = () => {
    const payload = {
      week: computedWeek,
      amount: Number(amount),
      date,
      status,
      note,
      // Payer is always recorded — they're the investor who created
      // (or owns) the weekly payment, independent of paid/pending state.
      paidByPersonId: paidByPersonId || null,
    };
    if (status === "paid") {
      payload.paidDate = paidDate || today;
    } else {
      payload.paidDate = "";
    }
    if (isEdit) {
      payload.id = existing.id;
      updateWeeklyPayment(modal.locationId, payload);
    } else {
      addWeeklyPayment(modal.locationId, payload);
    }
    onClose();
  };

  return (
    <ModalShell onClose={onClose}>
      <ModalHeader title={isEdit ? "Edit Weekly Payment" : "Add Weekly Payment"} onClose={onClose} />
      <Field label="Week">
        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
          <button
            type="button"
            onClick={() => setWeekMode("calendar")}
            style={{ flex: 1, padding: "8px 10px", border: weekMode === "calendar" ? "1.5px solid #4F46E5" : "1px solid #E2E8F0", background: weekMode === "calendar" ? "#EEF2FF" : "white", color: weekMode === "calendar" ? "#4338CA" : "#64748B", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}
          >
            Calendar week
          </button>
          <button
            type="button"
            onClick={() => setWeekMode("custom")}
            style={{ flex: 1, padding: "8px 10px", border: weekMode === "custom" ? "1.5px solid #4F46E5" : "1px solid #E2E8F0", background: weekMode === "custom" ? "#EEF2FF" : "white", color: weekMode === "custom" ? "#4338CA" : "#64748B", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}
          >
            Custom label
          </button>
        </div>
        {weekMode === "calendar" ? (
          <WeekPicker value={weekAnchor} onChange={setWeekAnchor} placeholder="Pick any day in the week" />
        ) : (
          <input value={weekCustom} onChange={e => setWeekCustom(e.target.value)} placeholder="e.g. Sep 22 - Sep 28 / Week 39 / Holiday Week" style={inputStyle} />
        )}
      </Field>
      <Field label="Amount ($)"><input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" style={inputStyle} /></Field>
      <Field label="Logged Date"><DatePicker value={date} onChange={setDate} /></Field>
      <Field label="Paid By">
        {investorPool.length === 0 ? (
          <div style={{ fontSize: 11, color: "#92400E", padding: 8, background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 8 }}>
            No investors are attached to this location yet. Edit the location to add some first.
          </div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {investorPool.map(p => {
              const isOn = paidByPersonId === p.id;
              const color = colorForPerson(p.id, people);
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPaidByPersonId(p.id)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "5px 10px 5px 5px",
                    border: isOn ? `1.5px solid ${color.accent}` : "1px solid #E2E8F0",
                    background: isOn ? color.bg : "white",
                    color: isOn ? color.text : "#64748B",
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: FONT,
                  }}
                >
                  <Avatar personId={p.id} size={20} people={people} />
                  {personLabel(p, activeCompanyId)}
                </button>
              );
            })}
          </div>
        )}
      </Field>
      <Field label="Status">
        <div style={{ display: "flex", gap: 6 }}>
          <button
            type="button"
            onClick={() => setStatus("pending")}
            style={{ flex: 1, padding: "10px", border: status === "pending" ? "1.5px solid #F59E0B" : "1px solid #E2E8F0", background: status === "pending" ? "#FEF3C7" : "white", color: status === "pending" ? "#92400E" : "#64748B", borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}
          >
            Pending
          </button>
          <button
            type="button"
            onClick={() => setStatus("paid")}
            style={{ flex: 1, padding: "10px", border: status === "paid" ? "1.5px solid #10B981" : "1px solid #E2E8F0", background: status === "paid" ? "#ECFDF5" : "white", color: status === "paid" ? "#065F46" : "#64748B", borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}
          >
            Paid
          </button>
        </div>
        <div style={{ fontSize: 11, color: "#64748B", marginTop: 6 }}>
          Only payments marked Paid count toward the location's recovered total.
        </div>
      </Field>
      {status === "paid" && (
        <Field label="Paid Date"><DatePicker value={paidDate} onChange={setPaidDate} /></Field>
      )}
      <Field label="Note (optional)">
        <textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="Any extra context for this weekly payment"
          rows={3}
          style={{ ...inputStyle, resize: "vertical", minHeight: 64 }}
        />
      </Field>
      <PrimaryBtn onClick={handleSave} disabled={!valid}>{isEdit ? "Save Changes" : "Add Payment"}</PrimaryBtn>
    </ModalShell>
  );
}

// Workspace-level expense description directory. Drives the Add Expense
// dropdown. Stored in localStorage per company; admin can add/remove.
function ExpenseDescriptionsModal({ onClose, descriptions, onChange }) {
  const [draft, setDraft] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const trimmed = draft.trim();
  const exists = trimmed.length > 0 && descriptions.some(d => d.toLowerCase() === trimmed.toLowerCase());
  const canAdd = trimmed.length > 0 && !exists;

  const add = () => {
    if (!canAdd) return;
    onChange([...descriptions, trimmed]);
    setDraft("");
  };
  const remove = (label) => {
    onChange(descriptions.filter(d => d !== label));
  };
  const restoreDefaults = () => {
    onChange([...DEFAULT_EXPENSE_DESCRIPTIONS]);
  };

  if (confirmDelete) {
    return (
      <ConfirmDeleteModal
        title="Delete Expense Description"
        message={<>Remove <b>{confirmDelete}</b> from the dropdown? Existing expenses already saved with this description are not affected.</>}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => { remove(confirmDelete); setConfirmDelete(null); }}
      />
    );
  }

  return (
    <ModalShell onClose={onClose}>
      <ModalHeader title="Expense Descriptions" onClose={onClose} />
      <div style={{ fontSize: 12, color: "#64748B", marginBottom: 12, lineHeight: 1.5 }}>
        These options drive the <b>Description</b> dropdown when logging an expense. <b>{GENERAL_CONTRACTOR_DESCRIPTION}</b> is special — picking it lets the user assign a contractor so the expense goes under that vendor.
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") add(); }}
          placeholder="e.g. Pest Control"
          style={{ ...inputStyle, flex: 1 }}
        />
        <button
          type="button"
          onClick={add}
          disabled={!canAdd}
          style={{ padding: "0 16px", background: canAdd ? "#4F46E5" : "#CBD5E1", color: "white", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: canAdd ? "pointer" : "not-allowed", fontFamily: FONT }}
        >
          Add
        </button>
      </div>
      {exists && (
        <div style={{ fontSize: 11, color: "#92400E", background: "#FEF3C7", border: "1px solid #FCD34D", borderRadius: 8, padding: "6px 10px", marginBottom: 10 }}>
          Already in the list.
        </div>
      )}
      {descriptions.length === 0 ? (
        <div style={{ background: "white", border: "1px dashed #E2E8F0", borderRadius: 12, padding: 24, textAlign: "center", fontSize: 12, color: "#94A3B8" }}>
          No expense descriptions yet. Add one above or restore defaults.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {descriptions.map((d) => {
            const assignsContractor = isContractorDescription(d);
            return (
              <div key={d} style={{ background: "white", border: "1px solid #E2E8F0", borderLeft: `4px solid ${assignsContractor ? "#F97316" : "#10B981"}`, borderRadius: 10, padding: "10px 12px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#1E293B" }}>{d}</span>
                  {assignsContractor && <span style={{ fontSize: 11, fontWeight: 700, background: "#FFF7ED", color: "#9A3412", padding: "2px 7px", borderRadius: 4 }}>ASSIGNS A CONTRACTOR</span>}
                </div>
                <button
                  onClick={() => setConfirmDelete(d)}
                  style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 6, padding: "4px 8px", cursor: "pointer", color: "#991B1B", display: "flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, fontFamily: FONT }}
                >
                  {Icons.trash}
                </button>
              </div>
            );
          })}
        </div>
      )}
      <button
        type="button"
        onClick={restoreDefaults}
        style={{ width: "100%", marginTop: 14, padding: "10px", background: "#F1F5F9", color: "#475569", border: "none", borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}
      >
        Restore Defaults
      </button>
    </ModalShell>
  );
}

// Workspace-level contractor directory. Two views in one modal:
//   • list — saved contractors with name/trade/phone, an Auto-show
//     toggle, and Edit/Delete. Auto-show ON means the contractor pops
//     up in the location-level "quick pick" when adding contractors.
//   • form — add or edit one entry.
function SavedContractorsModal({ onClose, savedContractors, locations = [], upsert, remove, contractorCategories = [], addContractorCategory }) {
  const [view, setView] = useState("list");
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [name, setName] = useState("");
  const [trade, setTrade] = useState("");
  const [phone, setPhone] = useState("");
  const [autoShow, setAutoShow] = useState(true);

  // Roll up every contractor that already exists on a shared location
  // (with the location they live on, deduped). Anything that doesn't
  // already appear in `savedContractors` (matched on name+trade) gets
  // surfaced as a one-click import target so the user doesn't have to
  // retype contractors they entered before this directory existed.
  const sharedLocationContractors = (() => {
    const out = new Map();
    locations.forEach(loc => {
      if (loc.type !== "shared") return;
      (loc.contractors || []).forEach(c => {
        const key = `${(c.name || "").toLowerCase().trim()}|${(c.trade || "").toLowerCase().trim()}`;
        if (!out.has(key)) {
          out.set(key, {
            key,
            name: c.name || "",
            trade: c.trade || "",
            phone: c.phone || "",
            firstLocationName: loc.name,
            locationCount: 1,
          });
        } else {
          out.get(key).locationCount += 1;
        }
      });
    });
    return Array.from(out.values());
  })();
  const savedKey = (sc) => `${(sc.name || "").toLowerCase().trim()}|${(sc.trade || "").toLowerCase().trim()}`;
  const savedKeys = new Set(savedContractors.map(savedKey));
  const importable = sharedLocationContractors
    .filter(c => !savedKeys.has(c.key))
    .sort((a, b) => a.name.localeCompare(b.name));

  const openCreate = () => {
    setEditing(null);
    setName("");
    setTrade("");
    setPhone("");
    setAutoShow(true);
    setView("form");
  };
  const openEdit = (sc) => {
    setEditing(sc);
    setName(sc.name || "");
    setTrade(sc.trade || "");
    setPhone(sc.phone || "");
    setAutoShow(sc.autoShow !== false);
    setView("form");
  };
  const submit = () => {
    if (!name.trim()) return;
    const payload = { name: name.trim(), trade: trade.trim(), phone: phone.trim(), autoShow };
    if (editing) payload.id = editing.id;
    upsert(payload);
    setView("list");
  };
  const toggleAutoShow = (sc) => {
    upsert({ id: sc.id, name: sc.name, trade: sc.trade || "", phone: sc.phone || "", autoShow: !(sc.autoShow !== false) });
  };

  const sorted = [...savedContractors].sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  if (confirmDelete) {
    return (
      <ConfirmDeleteModal
        title="Delete Saved Contractor"
        message={<>Remove <b>{confirmDelete.name}</b> from your saved contractors? Locations that already use this contractor are not affected.</>}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => { remove(confirmDelete.id); setConfirmDelete(null); }}
      />
    );
  }

  if (view === "form") {
    return (
      <ModalShell onClose={onClose}>
        <ModalHeader title={editing ? "Edit Saved Contractor" : "New Saved Contractor"} onClose={onClose} />
        <Field label="Name"><input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Lalo Mitchell" style={inputStyle} /></Field>
        <Field label="Category">
          <CategorySelect value={trade} onChange={setTrade} categories={contractorCategories} onAddCategory={addContractorCategory} />
        </Field>
        <Field label="Phone"><input value={phone} onChange={e => setPhone(e.target.value)} placeholder="(832) 555-0100" style={inputStyle} /></Field>
        <Field label="Auto-show in locations">
          <button
            type="button"
            onClick={() => setAutoShow(!autoShow)}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 12px",
              border: "1px solid #E2E8F0",
              background: autoShow ? "#ECFDF5" : "white",
              borderRadius: 10,
              cursor: "pointer",
              fontFamily: FONT,
            }}
          >
            <div style={{ textAlign: "left" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: autoShow ? "#065F46" : "#64748B" }}>{autoShow ? "On" : "Off"}</div>
              <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 2 }}>
                {autoShow ? "Featured in the location quick-pick" : "Saved for one-off use only"}
              </div>
            </div>
            <div style={{ width: 36, height: 20, borderRadius: 10, background: autoShow ? "#10B981" : "#CBD5E1", position: "relative" }}>
              <div style={{ position: "absolute", top: 2, left: autoShow ? 18 : 2, width: 16, height: 16, borderRadius: "50%", background: "white", transition: "left 0.15s" }} />
            </div>
          </button>
        </Field>
        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
          <button type="button" onClick={() => setView("list")} style={{ flex: 1, padding: "12px", background: "#F1F5F9", color: "#475569", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>Cancel</button>
          <button type="button" onClick={submit} disabled={!name.trim()} style={{ flex: 1, padding: "12px", background: name.trim() ? "#4F46E5" : "#CBD5E1", color: "white", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: name.trim() ? "pointer" : "not-allowed", fontFamily: FONT }}>{editing ? "Save Changes" : "Add Contractor"}</button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell onClose={onClose}>
      <ModalHeader title="Saved Contractors" onClose={onClose} />
      <div style={{ fontSize: 12, color: "#64748B", marginBottom: 12, lineHeight: 1.5 }}>
        Save contractors once and reuse them across locations. Toggle <b>Auto-show</b> off for one-time contractors so they don't clutter every location's list.
      </div>
      <button type="button" onClick={openCreate} style={{ width: "100%", marginBottom: 12, padding: "10px 14px", background: "#4F46E5", color: "white", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: FONT, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        Add Contractor
      </button>
      {importable.length > 0 && (
        <div style={{ marginBottom: 14, padding: 12, background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#9A3412" }}>Found in your shared locations</div>
              <div style={{ fontSize: 10, color: "#B45309", marginTop: 2 }}>
                {importable.length} contractor{importable.length !== 1 ? "s" : ""} not yet in your directory
              </div>
            </div>
            <button
              type="button"
              onClick={() => importable.forEach(c => upsert({ name: c.name, trade: c.trade, phone: c.phone, autoShow: true }))}
              style={{ background: "#9A3412", color: "white", border: "none", borderRadius: 8, padding: "6px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}
            >
              Add all
            </button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {importable.map(c => (
              <div key={c.key} style={{ background: "white", border: "1px solid #FED7AA", borderRadius: 8, padding: "8px 10px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#1E293B" }}>{c.name}</span>
                    {c.trade && <span style={{ fontSize: 10, fontWeight: 700, background: "#FFF7ED", color: "#9A3412", padding: "1px 5px", borderRadius: 4 }}>{c.trade}</span>}
                  </div>
                  <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 2 }}>
                    {c.phone ? `${c.phone} · ` : ""}from {c.firstLocationName}{c.locationCount > 1 ? ` +${c.locationCount - 1} more` : ""}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => upsert({ name: c.name, trade: c.trade, phone: c.phone, autoShow: true })}
                  style={{ background: "#FFF7ED", color: "#9A3412", border: "1px solid #FED7AA", borderRadius: 6, padding: "4px 10px", fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: FONT, flexShrink: 0 }}
                >
                  + Add
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {sorted.length === 0 ? (
        <div style={{ background: "white", border: "1px dashed #E2E8F0", borderRadius: 12, padding: 24, textAlign: "center", fontSize: 12, color: "#94A3B8" }}>
          No saved contractors yet.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {sorted.map(sc => {
            const isOn = sc.autoShow !== false;
            return (
              <div key={sc.id} style={{ background: "white", border: "1px solid #E2E8F0", borderLeft: `4px solid ${isOn ? "#10B981" : "#CBD5E1"}`, borderRadius: 10, padding: "10px 12px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 2 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#1E293B" }}>{sc.name}</span>
                    {sc.trade && <span style={{ fontSize: 10, fontWeight: 700, background: "#FFF7ED", color: "#9A3412", padding: "1px 6px", borderRadius: 4 }}>{sc.trade}</span>}
                    <span style={{ fontSize: 9, fontWeight: 700, background: isOn ? "#ECFDF5" : "#F1F5F9", color: isOn ? "#065F46" : "#64748B", padding: "1px 6px", borderRadius: 4 }}>
                      {isOn ? "AUTO-SHOW" : "ARCHIVED"}
                    </span>
                  </div>
                  {sc.phone && <div style={{ fontSize: 11, color: "#64748B" }}>{sc.phone}</div>}
                </div>
                <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                  <button
                    type="button"
                    onClick={() => toggleAutoShow(sc)}
                    title={isOn ? "Turn off auto-show" : "Turn on auto-show"}
                    style={{ width: 36, height: 20, borderRadius: 10, background: isOn ? "#10B981" : "#CBD5E1", border: "none", position: "relative", cursor: "pointer", marginRight: 4 }}
                  >
                    <div style={{ position: "absolute", top: 2, left: isOn ? 18 : 2, width: 16, height: 16, borderRadius: "50%", background: "white", transition: "left 0.15s" }} />
                  </button>
                  <button onClick={() => openEdit(sc)} style={{ background: "#EEF2FF", border: "1px solid #C7D2FE", borderRadius: 6, padding: "4px 6px", cursor: "pointer", color: "#4338CA", display: "flex", alignItems: "center" }}>{Icons.edit}</button>
                  <button onClick={() => setConfirmDelete(sc)} style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 6, padding: "4px 6px", cursor: "pointer", color: "#991B1B", display: "flex", alignItems: "center" }}>{Icons.trash}</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </ModalShell>
  );
}
